/**
 * Shot detection + form scoring. Pure functions only - no DOM, no framework,
 * no MediaPipe types. Feed it landmark arrays, get shots back.
 *
 * Swapping basketball for another sport means replacing TUNABLES and the
 * three score* functions. Nothing else in the app knows what a jump shot is.
 */

/* ========================================================================== *
 *  TUNABLES - THIS IS THE BLOCK YOU EDIT AGAINST REAL FOOTAGE                *
 *  Every number the scoring engine uses lives here. Nothing is hardcoded     *
 *  further down. See README "Tuning" for what each one does to your scores.  *
 * ========================================================================== */
export const TUNABLES = {
  // --- Shot detection: when does a motion count as an attempt? ---
  // Wrist must rise this far above the shoulder (in shoulder-widths) to arm a shot.
  ARM_RISE: 0.15,
  // Elbow angle (degrees) above which the arm counts as extended = released.
  RELEASE_ELBOW_ANGLE: 150,
  // Wrist must be at least this far above the nose (shoulder-widths) at release.
  RELEASE_ABOVE_NOSE: 0.0,
  // How long the arm must stay up before a release counts. Was 3 frames,
  // which is 66 ms at 30 fps and 200 ms at 15: in time, a phone and a
  // laptop arm the same shot.
  MIN_ARMED_MS: 60,
  // Landmark visibility below this is treated as "can't see the joint".
  MIN_VISIBILITY: 0.5,
  // Ignore a new attempt within this many ms of the last one (debounce).
  // Was 700. A real session on 2026-09-22 logged four "attempts" inside four
  // seconds, which no human does; they were jitter. Nobody gets a rebound,
  // sets and shoots again inside a second and a half.
  SHOT_COOLDOWN_MS: 1500,
  // Shoulder width below this fraction of the frame means the player is too
  // small on screen to score: at that size the landmarks wobble by more than
  // the geometry being measured. Skipped with reason 'too-far', so the screen
  // can say "step closer" instead of scoring noise. GUESS, from one session:
  // the phantom reps came in with shoulder widths under about 0.045.
  MIN_SHOULDER_FRAC: 0.045,
  // The same rule in camera pixels, used once the detector knows the frame's
  // width (setFrameWidth). The wobble came from too few pixels on the body,
  // not from the body's share of the frame: since the far player is read from
  // a crop of the camera's own pixels (lockcrop.js), a sharper camera sees
  // farther. 24 px of shoulders is a body about 90 px tall. Measured with
  // scripts/far-eval.mjs on 2026-09-30: at 36 px a player at a sixth of the
  // frame on a 1440p camera scored 2 shots, at 24 px 3 (the clip holds about 4),
  // with no phantom shots at any size.
  MIN_SHOULDER_PX: 24,
  // An elbow more than one shoulder width off the wrist is not an arm. The same
  // session recorded 2.4. A release that reads like that is a mislabelled
  // landmark and is dropped rather than scored.
  MAX_ELBOW_OFFSET: 1.0,
  // The shooting wrist has to be genuinely seen at the moment of release.
  MIN_RELEASE_VIS: 0.6,
  // After the release the arm has to be seen still up on at least this many
  // frames before it drops, or it was not a shot. An arm cannot go from full
  // extension to hanging in one frame, but a landmark can: on 2026-09-25 the
  // pose model read a girl and her father's raised fist as one body, the
  // "arm" jumped up and down between frames, and that counted as a shot on 5
  // of 8 passes of the clip, every one with no follow through at all. Every
  // real shot in the clips replayed that day held its follow through for 26
  // frames or more. A shot with a poor follow through still counts, scored low.
  MIN_FOLLOW_FRAMES: 1,

  // --- Metric 1: elbow alignment (elbow under the ball) ---
  // Horizontal elbow-to-wrist offset, in shoulder-widths. 0 = perfectly stacked.
  ELBOW_OFFSET_PERFECT: 0.08,
  ELBOW_OFFSET_ZERO: 0.45,

  // --- Metric 2: release angle (forearm angle above horizontal at release) ---
  RELEASE_ANGLE_IDEAL: 62,
  // Degrees of deviation from ideal that drops the score to zero.
  RELEASE_ANGLE_ZERO_AT: 40,

  // --- Metric 3: follow-through (holding the arm up after release) ---
  // Held this long scores 100. Was 12 frames, which is 0.4 s at 30 fps and
  // 0.8 s at 15 fps: the same hold scored half as much on a slow phone.
  FOLLOW_MS_IDEAL: 400,
  // Elbow must stay at least this extended for a frame to count as held.
  FOLLOW_ELBOW_ANGLE: 140,

  // --- Overall ---
  // Weights must sum to 1.
  WEIGHTS: { elbow: 0.4, release: 0.35, follow: 0.25 },

  // --- Elements: how aligned a rep is with the best in the sport, as a tier ---
  // Fire = most aligned. Air = furthest away and the wind to set sail toward.
  // Thresholds are on the overall alignment score (0-100).
  ELEMENTS: [
    { id: 'fire',        min: 85, label: 'Fire',        line: 'On fire. That is the form.',           color: '#ff6a1f' },
    { id: 'electricity', min: 70, label: 'Electricity', line: 'Sparking. Heating up.',                color: '#ffd23f' },
    { id: 'earth',       min: 55, label: 'Earth',       line: 'Grounded. Solid base, build on it.',  color: '#8bc34a' },
    { id: 'water',       min: 40, label: 'Water',       line: 'Finding the flow. Keep moving.',      color: '#2ea8ff' },
    { id: 'air',         min: 0,  label: 'Air',         line: 'Set your sails. Every rep is wind.',  color: '#c9d6ff' },
  ],
  // Session element blends form alignment with makes once shots are judged.
  SESSION_ELEMENT_WEIGHTS: { form: 0.7, makes: 0.3 },

  /* ======================================================================= *
   *  POSE METRICS. READ THIS BEFORE YOU TRUST A NUMBER BELOW IT.            *
   *                                                                         *
   *  Every threshold from here down is an UNVALIDATED GUESS. Not one of     *
   *  them has been measured against a real player on a real court yet.      *
   *  buildBaseline() and calibratedTunables() replace the ones marked       *
   *  PERSONAL with the player's own measured range. Until a player has      *
   *  calibrated, any score built on these has to be shown as a rough        *
   *  guide and never as a grade. That is not a style note, it is the        *
   *  difference between a measurement and a made up number.                 *
   * ======================================================================= */

  // Shared gates for everything that reads the lower body.
  POSE: {
    // A landmark under this visibility counts as not seen.
    MIN_VISIBILITY: 0.5,
    // Mean visibility across the joints a detector needs before it scores.
    MIN_CONF: 0.55,
    // Fraction of the 33 landmarks that must be visible for the app to say
    // the camera can see enough to score anything. GUESS.
    FRAME_OK_COVERAGE: 0.55,
    // Torso length as a fraction of frame height, under which the player is
    // too far away for knees and ankles to be worth scoring. GUESS.
    FRAME_MIN_TORSO: 0.08,
    // A detector blind for this long gives up what it was holding. Was six
    // frames, which is 200 ms at 30 fps and 400 ms at 15.
    LOST_MS: 200,
    // Good frames in a row before a detector reads again after a blind one.
    // A joint flickering across the visibility line is neither seen nor
    // unseen, and one good frame in a run of bad ones must not move a verdict.
    RESUME_FRAMES: 3,
  },

  // Dribble. The camera sees the HAND, never the ball, so all of this is
  // hand motion and the UI has to say so.
  DRIBBLE: {
    SMOOTH_MS: 80,           // hand height averaged over this long before looking for a bounce. Was 3 frames at 30 fps
    // Hand travel between the top and bottom of one bounce, in torso
    // lengths. Under this it is jitter, not a dribble. GUESS.
    MIN_AMPLITUDE: 0.09,
    MIN_INTERVAL_MS: 170,    // closer together than this is one bounce. GUESS
    MAX_INTERVAL_MS: 1600,   // further apart than this and the run is over
    // Hand height at the bottom of the bounce, in torso lengths above the
    // hips. Below the hip scores 100, chest height scores 0. PERSONAL.
    LOW_BEST: -0.05,
    LOW_ZERO: 0.40,
    PACE_IDEAL_HZ: 2.2,      // bounces per second the pace score aims at. PERSONAL
    PACE_ZERO_AT_HZ: 1.3,    // deviation from that which scores zero. GUESS
    // How far one interval may sit from the running median, as a fraction of
    // it, before rhythm scores zero. GUESS.
    RHYTHM_ZERO_AT: 0.6,
    WEIGHTS: { control: 0.45, pace: 0.2, rhythm: 0.35 },
  },

  // Balance. The hip midpoint is a proxy for the centre of mass and the
  // ankles are a proxy for the base of support. Both are 2D and both depend
  // on the camera being roughly square to the player.
  BALANCE: {
    WINDOW_MS: 1500,         // one reading covers this much movement
    MIN_SAMPLES: 10,         // fewer than this and the window is thrown away
    // Hip offset from the middle of the feet, as a fraction of half the
    // distance between the ankles. 0 is centred, 1 is over one foot. GUESS.
    OFFSET_BEST: 0.25,
    OFFSET_ZERO: 1.15,
    SWAY_BEST: 0.06,         // frame to frame wobble of that offset. GUESS
    SWAY_ZERO: 0.45,
    LEAN_BEST: 8,            // trunk angle off vertical in degrees. GUESS
    LEAN_ZERO: 38,
    // Feet closer together than this (shoulder widths) make the base of
    // support meaningless, so the reading is marked low confidence.
    MIN_BASE: 0.35,
    WEIGHTS: { centre: 0.45, steady: 0.35, upright: 0.2 },
  },

  // Defensive stance.
  STANCE: {
    KNEE_ENTER: 152,         // mean knee angle at or under this = knees bent. GUESS
    KNEE_EXIT: 163,          // over this and the stance is over. GUESS
    WIDTH_ENTER: 1.10,       // ankles this many shoulder widths apart. GUESS
    WIDTH_EXIT: 0.85,        // feet closed to this fraction of WIDTH_ENTER and it is over. Was inline
    ARM_MS: 130,             // shape held this long before a stance counts, kills flicker. Was 5 frames at 30 fps
    EXIT_MS: 60,             // out of the shape this long and it is over. Was 3 frames at 30 fps
    MIN_HOLD_MS: 450,        // shorter than this is not a stance
    DEPTH_BEST: 118,         // mean knee angle scoring 100. PERSONAL
    DEPTH_ZERO: 166,
    WIDTH_BEST_LO: 1.25,     // width band that scores 100. GUESS
    WIDTH_BEST_HI: 1.75,
    WIDTH_ZERO_PAD: 0.55,    // how far outside that band scores zero
    // A wrist this far above the hips, in torso lengths, counts as a hand
    // up. It says nothing about whether the hands are active. GUESS.
    HANDS_UP: 0.10,
    HOLD_IDEAL_MS: 2500,     // holding this long scores 100 on hold. GUESS
    WEIGHTS: { depth: 0.4, width: 0.25, hands: 0.2, hold: 0.15 },
  },

  // Box out. Posture only. The camera cannot see contact, the ball, or who
  // is being held off, and the UI has to say that every time it shows one.
  BOXOUT: {
    WIDTH_ENTER: 1.25,       // wide base. GUESS
    KNEE_ENTER: 150,         // knees bent. GUESS
    ARMS_ENTER: 1.15,        // wrists apart over shoulder width. GUESS
    ARM_MS: 130,             // shape held this long before it counts. Was 5 frames at 30 fps
    EXIT_MS: 60,             // out of the shape this long and it is over. Was 3 frames at 30 fps
    MIN_HOLD_MS: 550,
    HOLD_IDEAL_MS: 2000,
    BASE_BEST: 1.6, BASE_ZERO: 1.0,      // ankle spread in shoulder widths. GUESS
    SINK_BEST: 125, SINK_ZERO: 166,      // mean knee angle. PERSONAL
    ARMS_BEST: 1.7, ARMS_ZERO: 0.9,      // wrist spread in shoulder widths. GUESS
    // Hip drop below the player's own standing hip height, in torso lengths.
    // This is the honest stand in for "hips back": a square on camera cannot
    // see depth, so sinking is what is actually measured. GUESS.
    DROP_BEST: 0.16, DROP_ZERO: 0.0,
    WEIGHTS: { base: 0.3, sink: 0.3, arms: 0.25, hold: 0.15 },
  },

  // First step and attack off the dribble.
  DRIVE: {
    START_SPEED: 0.55,       // hip speed, torso lengths per second, that starts one. GUESS
    STOP_SPEED: 0.25,
    STOP_MS: 60,             // under STOP_SPEED this long and it is over. Was 3 frames at 30 fps
    SPEED_MS: 80,            // hip speed is averaged over this long. Was 3 frames at 30 fps
    MIN_MS: 200, MAX_MS: 2500,
    COOLDOWN_MS: 800,
    // Ankle travel that counts as the first step having happened.
    STEP_TRIGGER: 0.12,
    STEP_BEST: 0.75, STEP_ZERO: 0.20,    // first step length in torso lengths. PERSONAL
    BURST_BEST: 1.9, BURST_ZERO: 0.5,    // peak hip speed. PERSONAL
    LOW_BEST: 128, LOW_ZERO: 172,        // knee angle at the push, lower is more coiled. PERSONAL
    WEIGHTS: { burst: 0.4, step: 0.35, low: 0.25 },
  },

  // Pivot. One foot planted, the other swinging around it. Every number is
  // a GUESS: no pivot has been measured on a real court yet. Distances are
  // in torso lengths unless they say otherwise.
  PIVOT: {
    WINDOW_MS: 350,          // how far back a foot's recent movement is judged over
    STILL_MAX: 0.06,         // plant point shifted under this over the window = planted. GUESS
    FREE_MIN: 0.20,          // free ankle path over the window that says it is swinging. GUESS
    STOP_MS: 200,            // the free foot is judged stopped over this much shorter look back
    FREE_STOP: 0.06,         // free ankle path over STOP_MS under this = it has stopped, pivot over. GUESS
    PLANT_EXIT: 0.10,        // plant point this far from where it was set = the foot moved. GUESS
    // A shift of the plant point counts only once it has held this long, the
    // least of the readings over the window: two frames at 15 fps, so one bad
    // landmark on one frame can neither end a pivot, say the foot moved, nor
    // cost the still score.
    MOVE_MS: 80,
    ARM_MS: 100,             // one foot still and the other swinging this long before it arms. Was 4 frames at 30 fps
    EXIT_MS: 60,             // over this long and it settles. Was 3 frames at 30 fps
    // Share of the arming frames that must agree on which foot is planted.
    // Under this the two feet are trading places, not pivoting. ARM_MS gives
    // 3 votes at 15 fps, 5 at 30 and 7 at 60, so the bar has to sit between
    // the even splits (3 of 5 is 0.6, 4 of 7 is 0.57) and one dissenter at
    // 15 fps (2 of 3 is 0.67). At 0.6 a 3 to 2 split passed and two jittering
    // feet armed a pivot at 30 fps. GUESS within that window.
    MIN_AGREE: 0.65,
    MIN_HOLD_MS: 500,        // shorter than this is a step, not a pivot
    STILL_BEST: 0.02, STILL_ZERO: 0.25,    // plant drift that scores 100 and 0. GUESS
    TRAVEL_BEST: 1.2, TRAVEL_ZERO: 0.2,    // free foot path over the hold. GUESS
    BALANCE_BEST: 0.15, BALANCE_ZERO: 0.8, // hip mid off the plant foot. GUESS
    WEIGHTS: { still: 0.45, turn: 0.3, balance: 0.25 },
  },

  // Defensive slide. Low, wide, moving across the picture, feet never
  // crossing. Sideways means across the frame, so the phone has to be in
  // front of or behind the player. GUESSES throughout.
  SLIDE: {
    KNEE_ENTER: 155,         // mean knee angle at or under this = low. GUESS
    KNEE_EXIT: 165,          // over this and the slide is over. GUESS
    WIDTH_ENTER: 1.0,        // ankles this many shoulder widths apart, outside the hips. GUESS
    WINDOW_MS: 400,          // lateral speed is the ankle-mid path over this long
    MOVE_START: 0.35,        // torso lengths per second across the frame that starts one. GUESS
    MOVE_STOP: 0.2,          // under this, or stood up, for EXIT_MS and it is over. GUESS
    WIDTH_EXIT: 0.85,        // feet closed to this fraction of WIDTH_ENTER count as together. Was inline
    ARM_MS: 100,             // shape held this long before it arms. Was 4 frames at 30 fps
    EXIT_MS: 60,             // stood up or stopped this long and it is over. Was 3 frames at 30 fps
    MIN_MS: 400,             // shorter than this is a shuffle, not a slide
    MAX_MS: 8000,            // longer rolls over into a new rep so the screen keeps up
    NARROW_MS: 300,          // feet together this long ends it; shorter is a cross, which is counted. Was 10 frames at 30 fps
    CROSS_DEAD: 0.05,        // ankles closer than this cannot be put in order
    DIR_MS: 200, DIR_DEAD: 0.04,        // a direction change is the ankle-mid travel over DIR_MS flipping sign, ignoring under DIR_DEAD
    LOW_BEST: 125, LOW_ZERO: 165,       // mean knee angle. PERSONAL
    WIDE_LO: 1.2, WIDE_HI: 2.0, WIDE_PAD: 0.5, // width band in shoulder widths that scores 100. GUESS
    CROSS_PENALTY: 40,       // points off noCross per crossing
    QUICK_BEST: 2.0, QUICK_ZERO: 0.3,   // lateral torso lengths per second. PERSONAL
    WEIGHTS: { low: 0.3, wide: 0.2, noCross: 0.25, quick: 0.25 },
  },

  // Footwork. Steps counted from one foot's sole rising above the other's
  // and coming back down. GUESSES throughout.
  FOOTWORK: {
    LIFT: 0.12,              // a sole this far above the other foot's, in torso lengths, is up. GUESS
    LAND: 0.06,              // back under this and it has landed
    MAX_AIR_MS: 1200,        // a foot up longer than this is held, not stepping
    GAP_MS: 900,             // no step for this long ends the run
    MAX_MS: 10000,           // a long drill rolls over into a new rep
    MIN_STEPS: 2,            // fewer is a shuffle, not footwork
    KNEE_MIN: 110,           // under this is a lunge or a squat, not upright. GUESS
    LEAN_MAX: 40,            // trunk this far off vertical is not upright. GUESS
    DIR_MS: 200, DIR_DEAD: 0.04,
    QUICK_BEST: 3.5, QUICK_ZERO: 1.0,      // steps per second. PERSONAL
    BALANCE_BEST: 0.10, BALANCE_ZERO: 0.6, // hips off the ankle midpoint. GUESS
    WEIGHTS: { quick: 0.55, balance: 0.45 },
  },

  // Fatigue drift. This is a comparison of a player with their own first
  // reps in the same session. It is never a verdict about their fitness and
  // it is never anything medical.
  FATIGUE: {
    BASELINE_REPS: 5,        // the player's own opening reps, per signal
    RECENT_REPS: 5,          // the reps compared against them
    MIN_SESSION_MS: 120000,  // under two minutes there is nothing to compare
    NOISE_PCT: 4,            // drift under this is noise, not a change. GUESS
    NOTICEABLE_PCT: 8,       // GUESS
    LARGE_PCT: 15,           // GUESS
  },

  // Calibration: how many reps buy a personal range instead of a guess.
  CALIBRATION: {
    SHOTS_NEEDED: 10,
    MADE_NEEDED: 4,          // made shots before the release angle is personalised
    STANCE_NEEDED: 3,
    DRIBBLE_NEEDED: 20,
    JUMP_NEEDED: 3,
    RELEASE_SPREAD_MIN: 18,  // how far the personal release band may close or open, degrees
    RELEASE_SPREAD_MAX: 45,
    FOLLOW_MIN_MS: 200, FOLLOW_MAX_MS: 830, // how far the personal follow through target may move. Were 6 and 25 frames at 30 fps
    MAX_AGE_DAYS: 90,        // older than this and the app asks for a fresh one
  },

  // Several bodies in frame. This is proximity tracking, not identity: two
  // players who cross will swap boxes and the UI must say so.
  TRACK: {
    // Ten, for five on five. What a phone can actually keep up with is a
    // separate question, answered at run time by the page measuring its own
    // frame rate, and it is fewer than ten on most phones today.
    MAX_SLOTS: 10,
    GATE: 0.22,              // how far a body may be from where it was EXPECTED, in frame widths. GUESS
    // How much of a body's last movement is assumed to continue. Two players
    // crossing are each still going the way they were going; matching on where
    // a body is heading, not where it was, is what stops them swapping. GUESS
    VELOCITY_KEEP: 0.7,
    // How much a difference in clothing colour counts against a match, against
    // distance. 0 turns the cue off. Colour describes a shirt, not a person:
    // it is never stored, and it is forgotten when the run ends. GUESS
    COLOUR_WEIGHT: 0.55,
    // A body that vanished and one that appeared are the same player only when
    // the clothing matches this closely AND no other vanished body comes close.
    // Both in the units of lookDistance(). Deliberately strict: a returning
    // player who is not recognised just gets a fresh box and a person names it,
    // which costs a tap. A returning player recognised as the WRONG person puts
    // a stranger's stats on a child's record, which costs a great deal more.
    REID_MAX: 0.10,
    REID_MARGIN: 0.09,
    REID_MS: 20000,          // how long after vanishing a shirt is still trusted
    DROP_MS: 1200,           // a slot nobody is in is retired after this
    // One body walks out of frame for a water break and comes back. With
    // exactly one body on screen the tracker picks the old slot back up
    // within this long, so a session does not shatter into ten strangers.
    // It is an assumption, it is flagged as resumed, and it is only ever
    // made when there is a single body: with two it would be a guess about
    // which of them came back.
    RESUME_MS: 900000,
  },
  // Which body is the player (createPlayerLock). Movement only: a body is a
  // place in the picture and a slot in the tracker, never a face.
  LOCK: {
    // The player's slot is empty: their body is not in view. For this long
    // nobody else is made the player, so a friend walking through the back
    // of the picture never takes over a session. It was 2500 in public/js/app.js.
    GONE_MS: 2500,
    // Unless they are alone. One body, nobody else tracked, that first showed
    // up at most this long before the player's slot went empty, and within
    // HANDOFF_DIST of where the player was: that is the player, found again by
    // the pose model a few frames later in a new place (a step toward the
    // phone, a body cut off by the edge of the picture), and it is the player
    // after HANDOFF_WAIT_MS instead of 2.5 seconds of no skeleton.
    HANDOFF_MS: 250,
    HANDOFF_DIST: 0.5,        // frame widths, hips to hips
    // The player's slot has to be out of view this long before the hand over.
    // A slot that misses one frame is usually back on the next: handed over at
    // once, the player went to the new slot, the old one came back and was
    // left out for 2.5 s, and a shot half read was dropped with it. Replayed
    // over the recorded clips on 2026-09-25, waiting 150 ms kept the player
    // on 99% of the frames of a clip played back and forth, against 75% with
    // no wait, and lost no shot.
    HANDOFF_WAIT_MS: 150,
  },
};
/* ===================== END TUNABLES ====================================== */

// MediaPipe Pose landmark indices.
export const LM = {
  NOSE: 0,
  L_SHOULDER: 11, R_SHOULDER: 12,
  L_ELBOW: 13, R_ELBOW: 14,
  L_WRIST: 15, R_WRIST: 16,
  // Lower body, used by the pose metrics below. Same MediaPipe numbering.
  L_HIP: 23, R_HIP: 24,
  L_KNEE: 25, R_KNEE: 26,
  L_ANKLE: 27, R_ANKLE: 28,
  L_HEEL: 29, R_HEEL: 30,
  L_FOOT: 31, R_FOOT: 32,
};

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const round = (n) => Math.round(n);

/** Interior angle at point b, in degrees. */
export function jointAngle(a, b, c) {
  const abx = a.x - b.x, aby = a.y - b.y;
  const cbx = c.x - b.x, cby = c.y - b.y;
  const dot = abx * cbx + aby * cby;
  const mag = Math.hypot(abx, aby) * Math.hypot(cbx, cby);
  if (mag === 0) return 0;
  return (Math.acos(clamp(dot / mag, -1, 1)) * 180) / Math.PI;
}

/* ---------------------------------------------------------- head direction
 * Which way the head is turned, from the nose against the two ears: nose on
 * the ear midpoint is a face square to the camera, nose pushed toward one
 * ear is a head turned that way. Positive means toward the larger x in
 * frame units (the player's own left when they face the camera). Only read
 * up close: the ears are a few pixels apart on a body thirty feet back and
 * the figure would be noise, so a small shoulder width returns null.
 */
export const HEAD = { MIN_SHOULDER_FRAC: 0.09, MIN_EAR_VIS: 0.5, SQUARE: 0.12 };
export function headYaw(lm, shoulderWidth) {
  const nose = lm?.[0], le = lm?.[7], re = lm?.[8];
  if (!nose || !le || !re) return null;
  if (Number.isFinite(shoulderWidth) && shoulderWidth < HEAD.MIN_SHOULDER_FRAC) return null;
  if ((le.visibility ?? 1) < HEAD.MIN_EAR_VIS || (re.visibility ?? 1) < HEAD.MIN_EAR_VIS) return null;
  const span = Math.abs(le.x - re.x);
  if (span < 1e-4) return null;
  return +((nose.x - (le.x + re.x) / 2) / span).toFixed(3);
}
/**
 * Were the eyes on the rim at release? Needs the rim mark and a head turned
 * enough to read. A face square to the camera says nothing about the rim
 * (it may be straight ahead) and returns null rather than a guess.
 */
export function eyesOnRim(metrics, rim) {
  if (!rim || !metrics || !Number.isFinite(metrics.headYaw) || !Number.isFinite(metrics.midX)) return null;
  if (Math.abs(metrics.headYaw) < HEAD.SQUARE) return null;
  const rimSide = Math.sign(rim.x - metrics.midX);
  if (!rimSide) return null;
  return Math.sign(metrics.headYaw) === rimSide ? 'on' : 'off';
}

/** Forearm angle above horizontal, in degrees (0 = flat, 90 = straight up). */
export function forearmAngle(elbow, wrist) {
  // Screen y grows downward, so negate to get "above horizontal".
  return (Math.atan2(-(wrist.y - elbow.y), Math.abs(wrist.x - elbow.x)) * 180) / Math.PI;
}

/** Linear score: `best` -> 100, `worst` -> 0. */
function ramp(value, best, worst) {
  if (worst === best) return 100;
  return round(clamp(100 * (1 - (value - best) / (worst - best)), 0, 100));
}

export function scoreElbow(elbowOffset, T = TUNABLES) {
  return ramp(Math.abs(elbowOffset), T.ELBOW_OFFSET_PERFECT, T.ELBOW_OFFSET_ZERO);
}

export function scoreRelease(releaseAngle, T = TUNABLES) {
  return ramp(Math.abs(releaseAngle - T.RELEASE_ANGLE_IDEAL), 0, T.RELEASE_ANGLE_ZERO_AT);
}

export function scoreFollow(followMs, T = TUNABLES) {
  return round(clamp((followMs / T.FOLLOW_MS_IDEAL) * 100, 0, 100));
}

export function scoreShot(metrics, T = TUNABLES) {
  const scores = {
    elbow: scoreElbow(metrics.elbowOffset, T),
    release: scoreRelease(metrics.releaseAngle, T),
    follow: scoreFollow(metrics.followMs, T),
  };
  const w = T.WEIGHTS;
  const overall = round(
    scores.elbow * w.elbow + scores.release * w.release + scores.follow * w.follow,
  );
  return { scores, overall };
}

/**
 * Frame-by-frame shot detector.
 *
 * `push(landmarks, timestampMs)` returns a completed shot object on the frame
 * the shot finishes, otherwise null. `state` is exposed for the live HUD.
 */
export function createShotDetector(T = TUNABLES) {
  const gate = createGate(T);
  let state = 'idle';        // idle -> armed -> released
  let armedAt = 0;
  let followFrames = 0, heldUntil = 0;
  let release = null;
  let lastShotAt = -Infinity;
  let side = 'right';
  let last = null;           // per-frame features, for the debug HUD and trace
  // After a shot the arm has to come DOWN before another can arm. Without this
  // an arm that stays up and wobbles re-arms itself on the wobble: that is where
  // the bursts of phantom attempts came from. It starts TRUE: a shot begins from
  // a set position, so the arm has to be seen down once before the first one can
  // arm. Otherwise a player who walks into frame mid-wave logs an attempt.
  let needDrop = true;
  let framePx = 0;           // the camera frame's width in pixels, when known

  function visible(p) {
    return p && (p.visibility === undefined || p.visibility >= T.MIN_VISIBILITY);
  }

  function reset() {
    state = 'idle';
    armedAt = 0;
    followFrames = 0; heldUntil = 0;
    release = null;
  }
  /** A full reset, for a new session: the arm has to be seen down again first. */
  function hardReset() { reset(); needDrop = true; lastShotAt = -Infinity; gate.reset(); }

  return {
    get state() { return state; },
    get side() { return side; },
    get last() { return last; },
    reset: hardReset,
    /** The camera frame's width in pixels: the too-far rule is then in pixels. */
    setFrameWidth(px) { framePx = Number.isFinite(px) && px > 0 ? px : 0; },

    push(lm, t) {
      if (!lm || lm.length < 17) { last = { t, skip: 'no-pose' }; return null; }

      const ls = lm[LM.L_SHOULDER], rs = lm[LM.R_SHOULDER], nose = lm[LM.NOSE];
      // Shooting hand = whichever visible wrist is higher on screen, chosen
      // while idle and then kept for the whole shot. Picked afresh every
      // frame, one blurred frame of the shooting wrist handed the shot to the
      // lowered guide hand and reset it, and the shot was read again late.
      const lw = lm[LM.L_WRIST], rw = lm[LM.R_WRIST];
      const lOk = visible(lw), rOk = visible(rw);
      const inShot = state !== 'idle';
      const useLeft = inShot ? side === 'left' : lOk && (!rOk || lw.y < rw.y);
      const elbow = useLeft ? lm[LM.L_ELBOW] : lm[LM.R_ELBOW];
      // A frame without the joints this needs keeps the state it has: a wrist
      // that leaves the top of the picture at release comes back down into a
      // shot still in progress, and it is never given up on. After a blind
      // patch the first few good frames are waited out (createGate), except
      // mid shot, where waiting reads the release frames late.
      const sees = visible(ls) && visible(rs) && (inShot ? (useLeft ? lOk : rOk) : (lOk || rOk)) && visible(elbow);
      if (gate.judge(sees, t, { eager: inShot }) !== 'read') {
        last = { t, skip: !visible(ls) || !visible(rs) ? 'no-shoulders' : !lOk && !rOk ? 'no-wrist' : !visible(elbow) ? 'no-elbow' : 'settling' };
        return null;
      }

      const shoulderWidth = Math.hypot(ls.x - rs.x, ls.y - rs.y) || 1e-6;
      if (framePx ? shoulderWidth * framePx < T.MIN_SHOULDER_PX : shoulderWidth < T.MIN_SHOULDER_FRAC) {
        // Too small to score. A shot in progress at this size is noise too.
        if (state !== 'idle') reset();
        last = { t, skip: 'too-far', shoulderWidth };
        return null;
      }
      side = useLeft ? 'left' : 'right';
      const wrist = useLeft ? lw : rw;
      const shoulder = useLeft ? ls : rs;

      // Height above shoulder, normalized so distance from camera doesn't matter.
      const rise = (shoulder.y - wrist.y) / shoulderWidth;
      const elbowAngle = jointAngle(shoulder, elbow, wrist);
      const noseRise = visible(nose) ? (nose.y - wrist.y) / shoulderWidth : null;

      last = {
        t, state, side, rise, elbowAngle, noseRise,
        // 0..1 how far the arm is toward a full release, for the live meter
        progress: clamp((elbowAngle - 90) / (T.RELEASE_ELBOW_ANGLE - 90), 0, 1),
        wristX: wrist.x,
        elbowOffset: (elbow.x - wrist.x) / shoulderWidth,
        forearm: forearmAngle(elbow, wrist),
        wristVis: wrist.visibility ?? 1,
        wristY: wrist.y,
      };

      if (state === 'idle') {
        if (needDrop && rise < T.ARM_RISE * 0.5) needDrop = false;
        if (!needDrop && rise >= T.ARM_RISE && t - lastShotAt > T.SHOT_COOLDOWN_MS) {
          state = 'armed';
          armedAt = t;
        }
        return null;
      }

      if (state === 'armed') {
        const aboveNose = visible(nose)
          ? (nose.y - wrist.y) / shoulderWidth >= T.RELEASE_ABOVE_NOSE
          : true;
        if (
          t - armedAt >= T.MIN_ARMED_MS &&
          elbowAngle >= T.RELEASE_ELBOW_ANGLE &&
          aboveNose
        ) {
          const offset = (elbow.x - wrist.x) / shoulderWidth;
          if (Math.abs(offset) > T.MAX_ELBOW_OFFSET || (wrist.visibility ?? 1) < T.MIN_RELEASE_VIS) {
            // Not an arm, or not really seen. Drop it, do not score it.
            last = { ...last, skip: 'implausible', elbowOffset: offset };
            reset();
            return null;
          }
          state = 'released';
          followFrames = 0; heldUntil = t;
          release = {
            elbowOffset: (elbow.x - wrist.x) / shoulderWidth,
            releaseAngle: forearmAngle(elbow, wrist),
            // Where the head pointed and where the body stood, for the eyes-on-rim call.
            headYaw: headYaw(lm, shoulderWidth),
            midX: +((ls.x + rs.x) / 2).toFixed(4),
            // How high the shooting wrist sat at release, in shoulder widths
            // above the shoulder. The fatigue tracker compares this figure
            // with the player's own first reps, so it has to be recorded here.
            rise,
            at: t,
          };
        } else if (rise < T.ARM_RISE * 0.5) {
          reset(); // arm came down without ever extending - not a shot
        }
        return null;
      }

      // state === 'released': how long the arm stays up and extended. Scored
      // in time; the frame count is kept because the server stores it.
      if (elbowAngle >= T.FOLLOW_ELBOW_ANGLE && rise >= T.ARM_RISE * 0.5) {
        followFrames++; heldUntil = t;
        return null;
      }

      // Arm dropped. Never seen held after the release: a landmark that
      // jumped, not an arm (MIN_FOLLOW_FRAMES). Dropped, not scored.
      if (followFrames < T.MIN_FOLLOW_FRAMES) {
        last = { ...last, skip: 'no-follow' };
        reset();
        return null;
      }

      // Arm dropped - the shot is complete.
      // Read everything off `release` before reset() clears it.
      const metrics = {
        elbowOffset: release.elbowOffset,
        releaseAngle: release.releaseAngle,
        releaseRise: release.rise,
        at: release.at,
        followFrames,
        followMs: heldUntil - release.at,
      };
      const release_headYaw = release.headYaw ?? null, release_midX = release.midX ?? null;
      const { scores, overall } = scoreShot(metrics, T);
      lastShotAt = t;
      needDrop = true;
      reset();
      return {
        metrics: {
          elbowOffset: +metrics.elbowOffset.toFixed(3),
          releaseAngle: round(metrics.releaseAngle),
          headYaw: release_headYaw, midX: release_midX,
          releaseRise: +metrics.releaseRise.toFixed(3),
          followFrames: metrics.followFrames,
          followMs: Math.round(metrics.followMs),
        },
        scores,
        overall,
        side,
        releasedAt: metrics.at,
        made: null,
      };
    },
  };
}

/** Session averages, used by the HUD and sent to the coach. */
export function summarize(shots) {
  const n = shots.length;
  if (!n) return { attempts: 0, makes: 0, pct: 0, avg: { elbow: 0, release: 0, follow: 0, overall: 0 } };
  const sum = (f) => shots.reduce((a, s) => a + f(s), 0);
  const makes = shots.filter((s) => s.made === true).length;
  const decided = shots.filter((s) => s.made !== null).length;
  return {
    attempts: n,
    makes,
    pct: decided ? Math.round((makes / decided) * 100) : 0,
    avg: {
      elbow: round(sum((s) => s.scores.elbow) / n),
      release: round(sum((s) => s.scores.release) / n),
      follow: round(sum((s) => s.scores.follow) / n),
      overall: round(sum((s) => s.overall) / n),
    },
  };
}

/** Element tier for an alignment score. */
export function elementFor(score, T = TUNABLES) {
  return T.ELEMENTS.find((e) => score >= e.min) || T.ELEMENTS.at(-1);
}

/** Session-level element: form alignment plus make rate once shots are judged. */
export function sessionElement(shots, T = TUNABLES) {
  const s = summarize(shots);
  if (!s.attempts) return null;
  const decided = shots.filter((x) => x.made !== null).length;
  const w = T.SESSION_ELEMENT_WEIGHTS;
  const blended = decided ? s.avg.overall * w.form + s.pct * w.makes : s.avg.overall;
  return { ...elementFor(Math.round(blended), T), score: Math.round(blended) };
}

/* ========================================================================== *
 *  POSE METRICS                                                              *
 *                                                                            *
 *  Everything below reads MediaPipe pose landmarks and nothing else. There   *
 *  is no ball in the picture, no hoop, no court model and no identity, so     *
 *  there is nothing here that counts a basket, a rebound or an assist. What   *
 *  a camera can honestly see is a body: where the joints are, how bent they   *
 *  are, how fast they move and how all of that changes across a session.      *
 *                                                                            *
 *  Every rep these detectors return carries source: 'measured', the raw       *
 *  metrics it was built from, and calibrated: true or false. A screen that    *
 *  shows a score is expected to show those too.                              *
 *                                                                            *
 *  Coordinates: MediaPipe gives x and y in 0 to 1 of the frame, y growing     *
 *  DOWNWARD. One "body unit" here is the torso length, shoulders to hips,     *
 *  so nothing depends on how far the player is standing from the phone.       *
 * ========================================================================== */

const mean = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);
function sorted(a) { return [...a].sort((x, y) => x - y); }
function percentile(values, p) {
  if (!values.length) return 0;
  const s = sorted(values);
  const i = clamp((s.length - 1) * p, 0, s.length - 1);
  const lo = Math.floor(i), hi = Math.ceil(i);
  return lo === hi ? s[lo] : s[lo] + (s[hi] - s[lo]) * (i - lo);
}
const median = (values) => percentile(values, 0.5);
function stdev(values) {
  if (values.length < 2) return 0;
  const m = mean(values);
  return Math.sqrt(mean(values.map((v) => (v - m) * (v - m))));
}

/** Score 100 anywhere inside [lo, hi], falling to 0 `pad` outside it. */
function plateau(value, lo, hi, pad) {
  if (value >= lo && value <= hi) return 100;
  const out = value < lo ? lo - value : value - hi;
  return ramp(out, 0, Math.max(pad, 1e-6));
}

/**
 * Weighted average that skips anything that could not be measured. A metric
 * the camera missed drops out and the rest are reweighted, so a missing
 * number never quietly scores zero.
 */
function weighted(scores, weights) {
  let total = 0, sum = 0;
  for (const [k, w] of Object.entries(weights)) {
    const v = scores[k];
    if (v === null || v === undefined || !Number.isFinite(v)) continue;
    total += w; sum += v * w;
  }
  return total > 0 ? round(sum / total) : null;
}

/** Is this landmark good enough to use? */
export function seen(p, T = TUNABLES) {
  return Boolean(p) && (p.visibility === undefined || p.visibility >= T.POSE.MIN_VISIBILITY);
}
const visOf = (p) => (p && p.visibility === undefined ? 1 : (p?.visibility ?? 0));
const midPoint = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, visibility: Math.min(visOf(a), visOf(b)) });

/* ------------------------------------------------------------ world points
 * MediaPipe also hands back worldLandmarks: the same 33 joints in metres,
 * with the origin between the hips and the axes the way the picture has
 * them, x to the right, y DOWN, z toward the camera negative. An angle or a
 * length measured in them does not change when the player walks away from
 * the phone or the phone sits low on the floor, which is exactly where the
 * picture numbers go wrong. They carry no position, though: the hips are
 * always at zero, so where a body IS in the picture, and how far it moved
 * across it, stay 2D. The page attaches them to the landmark array as
 * `lm.world`; a detector that finds none uses the picture.
 */
function worldOf(lm) {
  const w = lm?.world;
  if (!Array.isArray(w) || w.length < 33) return null;
  const h = w[LM.L_HIP];
  return h && Number.isFinite(h.x) && Number.isFinite(h.y) && Number.isFinite(h.z) ? w : null;
}
const dist3 = (a, b) => Math.hypot(a.x - b.x, a.y - b.y, (a.z || 0) - (b.z || 0));
const mid3 = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, z: ((a.z || 0) + (b.z || 0)) / 2 });
/** Distance along the floor, height ignored. */
const floorDist = (a, b) => Math.hypot(a.x - b.x, (a.z || 0) - (b.z || 0));
/** Interior angle at point b in three dimensions, degrees. */
function jointAngle3(a, b, c) {
  const abx = a.x - b.x, aby = a.y - b.y, abz = (a.z || 0) - (b.z || 0);
  const cbx = c.x - b.x, cby = c.y - b.y, cbz = (c.z || 0) - (b.z || 0);
  const dot = abx * cbx + aby * cby + abz * cbz;
  const mag = Math.hypot(abx, aby, abz) * Math.hypot(cbx, cby, cbz);
  if (mag === 0) return 0;
  return (Math.acos(clamp(dot / mag, -1, 1)) * 180) / Math.PI;
}
/** Ankle, heel and toe of one foot, whichever of them are seen, from the picture or the world points. */
function footOf(pts, side, T) {
  const idx = side === 'left' ? [LM.L_ANKLE, LM.L_HEEL, LM.L_FOOT] : [LM.R_ANKLE, LM.R_HEEL, LM.R_FOOT];
  const pick = (i) => (seen(pts[i], T) ? pts[i] : null);
  return { ankle: pick(idx[0]), heel: pick(idx[1]), toe: pick(idx[2]) };
}

/**
 * One frame of pose, turned into the features every detector below shares.
 * Returns { ok: false, reason } rather than throwing when the camera cannot
 * see enough, which happens constantly on a real court.
 */
export function poseFrame(lm, T = TUNABLES) {
  if (!Array.isArray(lm) || lm.length < 33) return { ok: false, reason: 'no-pose', conf: 0 };
  const ls = lm[LM.L_SHOULDER], rs = lm[LM.R_SHOULDER], lh = lm[LM.L_HIP], rh = lm[LM.R_HIP];
  if (![ls, rs, lh, rh].every((p) => seen(p, T))) return { ok: false, reason: 'no-torso', conf: 0 };

  const shoulderMid = midPoint(ls, rs), hipMid = midPoint(lh, rh);
  const shoulderWidth = Math.hypot(ls.x - rs.x, ls.y - rs.y) || 1e-6;
  const torso = Math.hypot(shoulderMid.x - hipMid.x, shoulderMid.y - hipMid.y);
  // One body unit. Torso is the steadier of the two, shoulder width collapses
  // when a player turns side on, so fall back only when torso is unusable.
  const scale = Math.max(torso, shoulderWidth * 0.6, 1e-4);

  const lk = lm[LM.L_KNEE], rk = lm[LM.R_KNEE], la = lm[LM.L_ANKLE], ra = lm[LM.R_ANKLE];
  const legs = [lk, rk, la, ra].every((p) => seen(p, T));
  const ankleMid = legs ? midPoint(la, ra) : null;
  const lw = lm[LM.L_WRIST], rw = lm[LM.R_WRIST];
  const armL = seen(lw, T), armR = seen(rw, T);

  // The shape of the body: angles, widths and heights. In metres from the
  // world points when the page supplied them, else from the picture, where
  // a low camera or a long way back bends every one of them.
  const W = worldOf(lm);
  const P = W || lm;
  const angle = W ? jointAngle3 : jointAngle;
  const sMid = W ? mid3(W[LM.L_SHOULDER], W[LM.R_SHOULDER]) : shoulderMid;
  const hMid = W ? mid3(W[LM.L_HIP], W[LM.R_HIP]) : hipMid;
  const sWidth = W ? (dist3(W[LM.L_SHOULDER], W[LM.R_SHOULDER]) || 1e-6) : shoulderWidth;
  const bodyScale = W ? Math.max(dist3(sMid, hMid), sWidth * 0.6, 1e-4) : scale;
  const across = W ? floorDist : (a, b) => Math.abs(a.x - b.x);
  const kneeL = legs ? angle(P[LM.L_HIP], P[LM.L_KNEE], P[LM.L_ANKLE]) : null;
  const kneeR = legs ? angle(P[LM.R_HIP], P[LM.R_KNEE], P[LM.R_ANKLE]) : null;
  const hipL = legs ? angle(P[LM.L_SHOULDER], P[LM.L_HIP], P[LM.L_KNEE]) : null;
  const hipR = legs ? angle(P[LM.R_SHOULDER], P[LM.R_HIP], P[LM.R_KNEE]) : null;
  const aMid = legs ? (W ? mid3(W[LM.L_ANKLE], W[LM.R_ANKLE]) : ankleMid) : null;

  // Degrees off vertical, sideways across the frame, and the sign says which
  // way. In the world too: the balance and footwork thresholds were set for
  // sideways lean, and counting lean toward the phone as well marked every
  // athletic stance facing the camera as off balance (25 degrees forward read
  // as 25 off vertical and cost the upright score more than half).
  const trunkLean = (Math.atan2(sMid.x - hMid.x, Math.max(hMid.y - sMid.y, 1e-6)) * 180) / Math.PI;

  const used = [ls, rs, lh, rh, lk, rk, la, ra].filter(Boolean);
  const conf = used.length ? mean(used.map(visOf)) : 0;

  return {
    ok: true, legs, armL, armR, conf, world: Boolean(W),
    shoulderMid, hipMid, ankleMid, shoulderWidth, torso, scale,
    // One body unit for the space the shape was read in: metres of torso
    // from the world points, else the same as `scale`.
    bodyScale,
    kneeL, kneeR, kneeMean: legs ? (kneeL + kneeR) / 2 : null,
    hipL, hipR, hipFlex: legs ? (hipL + hipR) / 2 : null,
    // Ankles apart, in shoulder widths. The base of support.
    ankleSpread: legs ? across(P[LM.L_ANKLE], P[LM.R_ANKLE]) / sWidth : null,
    // Wrists apart, in shoulder widths. Arms out.
    armSpread: armL && armR ? across(P[LM.L_WRIST], P[LM.R_WRIST]) / sWidth : null,
    // How high each hand sits above the hips, in torso lengths.
    handL: armL ? (hMid.y - P[LM.L_WRIST].y) / bodyScale : null,
    handR: armR ? (hMid.y - P[LM.R_WRIST].y) / bodyScale : null,
    // Hips above the feet, in torso lengths. Drops when the player sinks and
    // is not fooled by the player walking toward the camera.
    hipOverFeet: legs ? (aMid.y - hMid.y) / bodyScale : null,
    hipY: hipMid.y, hipX: hipMid.x,
    // Raw picture points a detector needs by hand. Null whenever the joint is not seen.
    ankleL: legs ? { x: la.x, y: la.y } : null,
    ankleR: legs ? { x: ra.x, y: ra.y } : null,
    wristL: armL ? { x: lw.x, y: lw.y } : null,
    wristR: armR ? { x: rw.x, y: rw.y } : null,
    trunkLean,
  };
}

/**
 * What the camera can and cannot do with the picture it has right now. The
 * reasons are keys, not sentences: the screen writes the English.
 */
export function frameQuality(lm, T = TUNABLES) {
  const out = { ok: false, coverage: 0, torso: 0, conf: 0, reasons: [], canScore: { upper: false, lower: false } };
  if (!Array.isArray(lm) || !lm.length) { out.reasons.push('nobody'); return out; }
  const visible = lm.filter((p) => seen(p, T)).length;
  out.coverage = +(visible / lm.length).toFixed(2);
  const f = poseFrame(lm, T);
  if (!f.ok) { out.reasons.push(f.reason === 'no-torso' ? 'torso' : 'nobody'); return out; }
  out.torso = +f.torso.toFixed(3);
  out.conf = +f.conf.toFixed(2);
  out.canScore.upper = Boolean(f.armL || f.armR);
  out.canScore.lower = Boolean(f.legs);
  if (f.torso < T.POSE.FRAME_MIN_TORSO) out.reasons.push('far');
  if (!f.legs) out.reasons.push('feet');
  if (!f.armL && !f.armR) out.reasons.push('arms');
  if (out.coverage < T.POSE.FRAME_OK_COVERAGE) out.reasons.push('partial');
  out.ok = out.reasons.length === 0;
  return out;
}

/**
 * A rolling reference level: the Nth percentile of a value over the last few
 * seconds. Used for "how high are this player's hips when they are just
 * standing there", which is the only honest way to measure a jump or a sink
 * without knowing how tall they are or how far away they are standing.
 */
export function createLevelTracker({ windowMs = 4000, p = 0.5 } = {}) {
  const ring = [];
  return {
    push(t, v) {
      if (!Number.isFinite(v)) return;
      ring.push({ t, v });
      while (ring.length && t - ring[0].t > windowMs) ring.shift();
      if (ring.length > 600) ring.shift();
    },
    get n() { return ring.length; },
    get level() { return ring.length ? percentile(ring.map((r) => r.v), p) : null; },
    reset() { ring.length = 0; },
  };
}

/* ------------------------------------------------------------ blind frames
 * Every detector below reads the picture through one of these. A frame the
 * camera cannot see enough of is not a frame where nothing happened, it is
 * a frame with no information in it, and the right thing to do with one is
 * to keep whatever the detector already believed. Held for LOST_MS, then
 * given up. A joint that flickers across the visibility line produces good
 * frames one at a time between bad ones, and those are not trusted either:
 * a detector reads again only after RESUME_FRAMES good frames in a row.
 *
 *   read   use the frame
 *   hold   keep the state, skip the frame
 *   lost   blind long enough that whatever was being held is over
 */
function createGate(T) {
  const G = T.POSE;
  // A detector starts out reading. The run of good frames is only owed after
  // a blind patch: counting it from the first frame threw away the first two,
  // which at 15 fps is the whole set position before a shot (133 ms).
  let good = G.RESUME_FRAMES, blindSince = null;
  return {
    reset() { good = G.RESUME_FRAMES; blindSince = null; },
    judge(sees, t, { eager = false } = {}) {
      if (!sees) {
        good = 0;
        if (blindSince === null) blindSince = t;
        return t - blindSince >= G.LOST_MS ? 'lost' : 'hold';
      }
      // The blind clock only stops once the resume run is complete. A joint
      // flickering good, bad, good never completes one, so the flicker adds up
      // to LOST_MS and the rep ends instead of being held for as long as it lasts.
      // eager skips the run: a shot mid release cannot wait two frames.
      if (!eager && good < G.RESUME_FRAMES) {
        good++;
        if (good < G.RESUME_FRAMES) return blindSince !== null && t - blindSince >= G.LOST_MS ? 'lost' : 'hold';
      }
      good = G.RESUME_FRAMES;
      blindSince = null;
      return 'read';
    },
  };
}
/** Enough of the lower body seen, and seen well enough, to read a frame. */
const seesLegs = (f, T) => f.ok && f.legs && f.conf >= T.POSE.MIN_CONF;

/* ------------------------------------------------------------------ dribble
 * The pose model has no ball in it. What is measured here is the HAND going
 * up and down: a bounce is a low point in the hand's height, a run of them
 * is a dribble. A hand pumping with no ball in it counts exactly the same,
 * and any screen showing this has to say so.
 */
export function createDribbleDetector(T = TUNABLES) {
  const D = T.DRIBBLE;
  const gate = createGate(T);
  const makeHand = (side) => ({ side, raw: [], sm: [], top: null });
  const hands = { left: makeHand('left'), right: makeHand('right') };
  const intervals = [];
  let lastHand = null, lastBounceAt = -Infinity, count = 0, last = null;

  function feed(h, t, v) {
    h.raw.push({ t, v });
    while (h.raw.length && t - h.raw[0].t > D.SMOOTH_MS) h.raw.shift();
    const s = mean(h.raw.map((r) => r.v));
    h.sm.push({ t, v: s });
    if (h.sm.length > 3) h.sm.shift();
    h.top = h.top === null ? s : Math.max(h.top, s);
    if (h.sm.length < 3) return null;
    const [a, b, c] = h.sm;
    if (!(b.v < a.v && b.v <= c.v)) return null;      // not a low point
    const amplitude = h.top - b.v;
    h.top = c.v;                                       // start the next bounce's peak here
    if (amplitude < D.MIN_AMPLITUDE) return null;
    return { at: lowPointAt(a, b, c), low: b.v, amplitude };
  }

  /**
   * When the hand was lowest, between frames. The low frame alone is off by
   * up to half a frame, 33 ms at 15 fps, and one interval from two of those
   * read a 2.2 Hz dribble as 2.5. A parabola through the three samples puts
   * the bottom where the motion put it. Times are taken relative to the low
   * frame so the arithmetic stays small.
   */
  function lowPointAt(a, b, c) {
    const x1 = a.t - b.t, x3 = c.t - b.t;
    const den = x1 * x3 * (x1 - x3);
    if (!den) return b.t;
    const A = (x3 * (a.v - b.v) - x1 * (c.v - b.v)) / den;
    const B = (x1 * x1 * (c.v - b.v) - x3 * x3 * (a.v - b.v)) / den;
    if (!(A > 0)) return b.t;
    const x = -B / (2 * A);
    return x > x1 && x < x3 ? b.t + x : b.t;
  }

  return {
    get last() { return last; },
    get count() { return count; },
    reset() {
      hands.left = makeHand('left'); hands.right = makeHand('right');
      intervals.length = 0; lastHand = null; lastBounceAt = -Infinity; count = 0; last = null; gate.reset();
    },

    push(lm, t) {
      const f = poseFrame(lm, T);
      const verdict = gate.judge(f.ok && (f.armL || f.armR), t);
      if (verdict !== 'read') {
        last = { t, skip: f.ok ? 'no-hands' : f.reason, count, conf: f.ok ? f.conf : 0 };
        // Blind long enough and the hand's recent history describes nothing: start it over.
        if (verdict === 'lost') { hands.left = makeHand('left'); hands.right = makeHand('right'); }
        return null;
      }
      let bounce = null, side = null;
      for (const key of ['left', 'right']) {
        const h = key === 'left' ? f.handL : f.handR;
        if (h === null) continue;
        const hit = feed(hands[key], t, h);
        // Two hands cannot bounce at once: take the lower of the two.
        if (hit && (!bounce || hit.low < bounce.low)) { bounce = hit; side = key; }
      }
      last = { t, handL: f.handL, handR: f.handR, count, conf: f.conf };
      if (!bounce) return null;
      if (bounce.at - lastBounceAt < D.MIN_INTERVAL_MS) return null;

      const gap = bounce.at - lastBounceAt;
      const intervalMs = Number.isFinite(gap) && gap <= D.MAX_INTERVAL_MS ? gap : null;
      if (intervalMs === null) intervals.length = 0;     // a new run, not the same one
      const alternated = Boolean(lastHand && lastHand !== side);
      const cadenceHz = intervalMs ? 1000 / intervalMs : null;

      const rhythm = intervals.length >= 2 && intervalMs
        ? ramp(Math.abs(intervalMs - median(intervals)) / Math.max(median(intervals), 1), 0, D.RHYTHM_ZERO_AT)
        : null;
      const scores = {
        control: ramp(bounce.low, D.LOW_BEST, D.LOW_ZERO),
        pace: cadenceHz === null ? null : ramp(Math.abs(cadenceHz - D.PACE_IDEAL_HZ), 0, D.PACE_ZERO_AT_HZ),
        rhythm,
      };
      if (intervalMs) { intervals.push(intervalMs); if (intervals.length > 20) intervals.shift(); }
      lastBounceAt = bounce.at; lastHand = side; count++;

      return {
        kind: 'dribble', at: bounce.at, source: 'measured', calibrated: Boolean(T.CALIBRATED),
        metrics: {
          hand: side,
          lowHeight: +bounce.low.toFixed(3),
          amplitude: +bounce.amplitude.toFixed(3),
          intervalMs: intervalMs === null ? null : Math.round(intervalMs),
          cadenceHz: cadenceHz === null ? null : +cadenceHz.toFixed(2),
          alternated, index: count, conf: +f.conf.toFixed(2),
        },
        scores,
        overall: weighted(scores, D.WEIGHTS),
      };
    },
  };
}

/* ------------------------------------------------------------------ balance
 * Hip midpoint against the middle of the feet, sampled through whatever the
 * player is doing, and reported once per window so there is a timeline rather
 * than one number at the end. Both are 2D proxies: the hips stand in for the
 * centre of mass and the ankles for the base of support.
 */
export function createBalanceDetector(T = TUNABLES) {
  const B = T.BALANCE;
  const gate = createGate(T);
  let startAt = null, offsets = [], leans = [], confs = [], narrow = 0, samples = 0, last = null;

  const clear = () => { startAt = null; offsets = []; leans = []; confs = []; narrow = 0; samples = 0; };

  return {
    get last() { return last; },
    reset() { clear(); gate.reset(); },

    push(lm, t) {
      const f = poseFrame(lm, T);
      const verdict = gate.judge(seesLegs(f, T), t);
      if (verdict !== 'read') {
        last = { t, skip: f.ok ? 'no-legs' : f.reason };
        // A window with a hole in it is not a window. Start again when the legs are back.
        if (verdict === 'lost') clear();
        return null;
      }
      if (startAt === null) startAt = t;

      const baseWidth = Math.abs(f.ankleL.x - f.ankleR.x);
      const minBase = B.MIN_BASE * f.shoulderWidth;
      const isNarrow = baseWidth < minBase;
      const halfBase = Math.max(baseWidth, minBase) / 2;
      const offset = (f.hipX - f.ankleMid.x) / halfBase;

      offsets.push(offset); leans.push(Math.abs(f.trunkLean)); confs.push(f.conf);
      if (isNarrow) narrow++;
      samples++;
      last = { t, offset: +offset.toFixed(2), lean: Math.round(f.trunkLean), narrow: isNarrow, samples };

      if (t - startAt < B.WINDOW_MS) return null;
      if (samples < B.MIN_SAMPLES) { clear(); return null; }

      const absOffsets = offsets.map(Math.abs);
      const peak = percentile(absOffsets, 0.9);
      const sway = stdev(offsets);
      const lean = mean(leans);
      const scores = {
        centre: ramp(peak, B.OFFSET_BEST, B.OFFSET_ZERO),
        steady: ramp(sway, B.SWAY_BEST, B.SWAY_ZERO),
        upright: ramp(lean, B.LEAN_BEST, B.LEAN_ZERO),
      };
      const rep = {
        kind: 'balance', at: t, source: 'measured', calibrated: Boolean(T.CALIBRATED),
        metrics: {
          offset: +peak.toFixed(2),
          meanOffset: +mean(absOffsets).toFixed(2),
          sway: +sway.toFixed(3),
          lean: Math.round(lean),
          outOfBasePct: Math.round((absOffsets.filter((v) => v > 1).length / samples) * 100),
          narrowPct: Math.round((narrow / samples) * 100),
          samples, windowMs: Math.round(t - startAt),
          conf: +mean(confs).toFixed(2),
        },
        scores,
        overall: weighted(scores, B.WEIGHTS),
      };
      clear();
      return rep;
    },
  };
}

/* ------------------------------------------------------------------- stance
 * A defensive stance: knees bent, feet wider than the shoulders, held. The
 * hands part only knows whether a wrist is above the hips. It does not know
 * whether the hands are doing anything useful. Frames after the player has
 * come out of the shape are not collected: standing up at the end would pull
 * the knee average up, and the hold runs to the last frame it was a stance.
 */
export function createStanceDetector(T = TUNABLES) {
  const S = T.STANCE;
  const gate = createGate(T);
  let armedAt = null, endAt = 0, inStance = false, overSince = null, last = null;
  let knees = [], widths = [], hips = [], confs = [], handsUp = 0, frames = 0;

  const clear = () => {
    armedAt = null; inStance = false; overSince = null;
    knees = []; widths = []; hips = []; confs = []; handsUp = 0; frames = 0;
  };

  function settle() {
    const holdMs = endAt - armedAt;
    const rep = holdMs >= S.MIN_HOLD_MS && frames > 0 ? build(holdMs) : null;
    clear();
    return rep;
  }

  function build(holdMs) {
    const kneeMean = mean(knees), widthRatio = mean(widths);
    const handsPct = frames ? handsUp / frames : 0;
    const scores = {
      depth: ramp(kneeMean, S.DEPTH_BEST, S.DEPTH_ZERO),
      width: plateau(widthRatio, S.WIDTH_BEST_LO, S.WIDTH_BEST_HI, S.WIDTH_ZERO_PAD),
      hands: round(handsPct * 100),
      hold: round(clamp((holdMs / S.HOLD_IDEAL_MS) * 100, 0, 100)),
    };
    return {
      kind: 'stance', at: endAt, source: 'measured', calibrated: Boolean(T.CALIBRATED),
      metrics: {
        holdMs: Math.round(holdMs),
        kneeMean: Math.round(kneeMean),
        kneeMin: Math.round(Math.min(...knees)),
        widthRatio: +widthRatio.toFixed(2),
        hipFlex: hips.length ? Math.round(mean(hips)) : null,
        handsUpPct: Math.round(handsPct * 100),
        frames, conf: +mean(confs).toFixed(2),
      },
      scores,
      overall: weighted(scores, S.WEIGHTS),
    };
  }

  return {
    get last() { return last; },
    get active() { return inStance; },
    reset() { clear(); gate.reset(); },

    push(lm, t) {
      const f = poseFrame(lm, T);
      const verdict = gate.judge(seesLegs(f, T), t);
      if (verdict !== 'read') {
        last = { t, skip: f.ok ? 'no-legs' : f.reason, active: inStance };
        if (verdict === 'lost') { if (inStance) return settle(); clear(); }
        return null;
      }
      const low = f.kneeMean <= S.KNEE_ENTER;
      const wide = f.ankleSpread >= S.WIDTH_ENTER;
      last = { t, knee: Math.round(f.kneeMean), width: +f.ankleSpread.toFixed(2), active: inStance, low, wide };

      if (!inStance) {
        if (low && wide) { armedAt ??= t; } else { armedAt = null; }
        if (armedAt !== null && t - armedAt >= S.ARM_MS) { inStance = true; overSince = null; endAt = t; }
        else return null;
      }

      // Out of the shape: held for EXIT_MS it is over, and none of it is collected.
      const over = f.kneeMean > S.KNEE_EXIT || f.ankleSpread < S.WIDTH_ENTER * S.WIDTH_EXIT;
      if (over) { overSince ??= t; if (t - overSince >= S.EXIT_MS) return settle(); return null; }
      overSince = null; endAt = t;

      knees.push(f.kneeMean); widths.push(f.ankleSpread); confs.push(f.conf);
      if (f.hipFlex !== null) hips.push(f.hipFlex);
      if ((f.handL !== null && f.handL >= S.HANDS_UP) || (f.handR !== null && f.handR >= S.HANDS_UP)) handsUp++;
      frames++;
      return null;
    },

    /** End of session: hand back a stance that was still being held. */
    flush() { return inStance ? settle() : null; },
  };
}

/* ------------------------------------------------------------------- boxout
 * Posture only. Wide base, knees sunk, arms out, held. The camera cannot see
 * contact, cannot see the ball, and cannot see who is being held off, so this
 * is never evidence that a box out happened in a game. It is evidence that a
 * body was in the shape of one.
 *
 * "Hips back" cannot be measured square on, so what stands in for it is the
 * hips dropping below this player's own standing height, which needs a
 * reference: pass standLevel() returning their standing hipOverFeet.
 */
export function createBoxoutDetector(T = TUNABLES, { standLevel = null } = {}) {
  const X = T.BOXOUT;
  const gate = createGate(T);
  let armedAt = null, endAt = 0, active = false, overSince = null, last = null;
  let knees = [], widths = [], arms = [], drops = [], leans = [], confs = [], frames = 0;

  const clear = () => {
    armedAt = null; active = false; overSince = null;
    knees = []; widths = []; arms = []; drops = []; leans = []; confs = []; frames = 0;
  };

  function build(holdMs) {
    const kneeMean = mean(knees), widthRatio = mean(widths), armSpread = mean(arms);
    const drop = drops.length ? percentile(drops, 0.9) : null;
    const scores = {
      base: ramp(widthRatio, X.BASE_BEST, X.BASE_ZERO),
      sink: ramp(kneeMean, X.SINK_BEST, X.SINK_ZERO),
      arms: ramp(armSpread, X.ARMS_BEST, X.ARMS_ZERO),
      hold: round(clamp((holdMs / X.HOLD_IDEAL_MS) * 100, 0, 100)),
    };
    return {
      kind: 'boxout', at: endAt, source: 'measured', calibrated: Boolean(T.CALIBRATED),
      metrics: {
        holdMs: Math.round(holdMs),
        kneeMean: Math.round(kneeMean),
        widthRatio: +widthRatio.toFixed(2),
        armSpread: +armSpread.toFixed(2),
        hipDrop: drop === null ? null : +drop.toFixed(3),
        trunkLean: leans.length ? Math.round(mean(leans.map(Math.abs))) : null,
        frames, conf: +mean(confs).toFixed(2),
      },
      scores,
      overall: weighted(scores, X.WEIGHTS),
      note: 'posture-only',
    };
  }

  function settle() {
    const holdMs = endAt - armedAt;
    const rep = holdMs >= X.MIN_HOLD_MS && frames > 0 ? build(holdMs) : null;
    clear();
    return rep;
  }

  return {
    get last() { return last; },
    get active() { return active; },
    reset() { clear(); gate.reset(); },
    flush() { return active ? settle() : null; },

    push(lm, t) {
      const f = poseFrame(lm, T);
      const verdict = gate.judge(seesLegs(f, T) && f.armSpread !== null, t);
      if (verdict !== 'read') {
        last = { t, skip: f.ok ? 'no-legs-or-arms' : f.reason, active };
        if (verdict === 'lost') { if (active) return settle(); clear(); }
        return null;
      }
      const shape = f.ankleSpread >= X.WIDTH_ENTER && f.kneeMean <= X.KNEE_ENTER && f.armSpread >= X.ARMS_ENTER;
      last = { t, width: +f.ankleSpread.toFixed(2), knee: Math.round(f.kneeMean), arms: +f.armSpread.toFixed(2), active, shape };

      if (!active) {
        if (shape) { armedAt ??= t; } else { armedAt = null; }
        if (armedAt !== null && t - armedAt >= X.ARM_MS) { active = true; overSince = null; endAt = t; }
        else return null;
      }

      if (!shape) { overSince ??= t; if (t - overSince >= X.EXIT_MS) return settle(); return null; }
      overSince = null; endAt = t;

      knees.push(f.kneeMean); widths.push(f.ankleSpread); arms.push(f.armSpread);
      leans.push(f.trunkLean); confs.push(f.conf); frames++;
      const stand = typeof standLevel === 'function' ? standLevel() : null;
      if (stand !== null && f.hipOverFeet !== null) drops.push(stand - f.hipOverFeet);
      return null;
    },
  };
}

/* -------------------------------------------------------------------- drive
 * A first step: the hips go from standing still to moving, one foot leaves,
 * the other one pushes. The camera sees the step, not the defender it was
 * taken against and not whether a ball came with it. Movement is read from
 * the picture: the world points have the hips at their origin and cannot see
 * a body go anywhere. The knee angle at the push comes from them.
 */
export function createDriveDetector(T = TUNABLES, { standHipY = null } = {}) {
  const V = T.DRIVE;
  const gate = createGate(T);
  let prev = null, speeds = [], last = null;
  let active = false, startAt = 0, lastEnd = -Infinity, stopSince = null, lastAt = 0;
  let startAnkleL = null, startAnkleR = null;
  let stepFoot = null, firstStepMs = null, travelL = 0, travelR = 0;
  let peakSpeed = 0, jumpRise = 0, dirX = 0, knees = [], confs = [];

  const clear = () => {
    active = false; stopSince = null;
    startAnkleL = null; startAnkleR = null; stepFoot = null; firstStepMs = null;
    travelL = 0; travelR = 0; peakSpeed = 0; jumpRise = 0; dirX = 0; knees = []; confs = [];
  };

  function build(durationMs) {
    const stepLen = stepFoot === 'left' ? travelL : stepFoot === 'right' ? travelR : Math.max(travelL, travelR);
    const kneePush = knees.length ? percentile(knees, 0.1) : null;
    const scores = {
      burst: ramp(peakSpeed, V.BURST_BEST, V.BURST_ZERO),
      step: stepLen > 0 ? ramp(stepLen, V.STEP_BEST, V.STEP_ZERO) : null,
      low: kneePush === null ? null : ramp(kneePush, V.LOW_BEST, V.LOW_ZERO),
    };
    return {
      kind: 'drive', at: lastAt, source: 'measured', calibrated: Boolean(T.CALIBRATED),
      metrics: {
        stepFoot,
        plantFoot: stepFoot === 'left' ? 'right' : stepFoot === 'right' ? 'left' : null,
        firstStepLen: +stepLen.toFixed(3),
        firstStepMs: firstStepMs === null ? null : Math.round(firstStepMs),
        durationMs: Math.round(durationMs),
        peakSpeed: +peakSpeed.toFixed(2),
        jumpRise: jumpRise > 0 ? +jumpRise.toFixed(3) : null,
        kneePush: kneePush === null ? null : Math.round(kneePush),
        // Which way across the frame. Left and right on screen are mirrored
        // for the player, so a screen should name the foot, not the side.
        dirX: dirX > 0 ? 1 : dirX < 0 ? -1 : 0,
        conf: confs.length ? +mean(confs).toFixed(2) : 0,
      },
      scores,
      overall: weighted(scores, V.WEIGHTS),
    };
  }

  function settle(t) {
    const durationMs = t - startAt;
    const rep = durationMs >= V.MIN_MS ? build(durationMs) : null;
    lastEnd = t;
    clear();
    return rep;
  }

  return {
    get last() { return last; },
    get active() { return active; },
    reset() { prev = null; speeds = []; lastEnd = -Infinity; clear(); gate.reset(); },
    flush() { return active ? settle(lastAt) : null; },

    push(lm, t) {
      const f = poseFrame(lm, T);
      const verdict = gate.judge(seesLegs(f, T), t);
      if (verdict !== 'read') {
        last = { t, skip: f.ok ? 'no-legs' : f.reason, active };
        if (verdict === 'lost') { prev = null; speeds = []; return active ? settle(lastAt) : null; }
        return null;
      }
      lastAt = t;

      let speed = null;
      if (prev) {
        const dt = t - prev.t;
        // A dropped frame is not a burst of speed. Ignore impossible gaps.
        if (dt > 0 && dt <= 200) {
          speed = (Math.hypot(f.hipX - prev.x, f.hipY - prev.y) / f.scale) / (dt / 1000);
          speeds.push({ t, v: speed });
          // Always keep the previous sample: below 12.5 fps the whole window
          // would otherwise hold one frame and a drive could never start.
          while (speeds.length > 2 && t - speeds[0].t > V.SPEED_MS) speeds.shift();
        }
      }
      prev = { t, x: f.hipX, y: f.hipY };
      const smooth = speeds.length ? mean(speeds.map((s) => s.v)) : 0;
      last = { t, speed: +smooth.toFixed(2), active, stepFoot };

      if (!active) {
        if (smooth >= V.START_SPEED && t - lastEnd >= V.COOLDOWN_MS && speeds.length >= 2) {
          active = true; startAt = t; stopSince = null;
          startAnkleL = f.ankleL; startAnkleR = f.ankleR;
          peakSpeed = smooth; knees = []; confs = [];
        }
        return null;
      }

      peakSpeed = Math.max(peakSpeed, smooth);
      confs.push(f.conf);
      if (f.kneeMean !== null) knees.push(f.kneeMean);
      if (startAnkleL && f.ankleL) travelL = Math.max(travelL, Math.abs(f.ankleL.x - startAnkleL.x) / f.scale);
      if (startAnkleR && f.ankleR) travelR = Math.max(travelR, Math.abs(f.ankleR.x - startAnkleR.x) / f.scale);
      if (!stepFoot) {
        if (travelL >= V.STEP_TRIGGER) { stepFoot = 'left'; firstStepMs = t - startAt; dirX = Math.sign(f.ankleL.x - startAnkleL.x); }
        else if (travelR >= V.STEP_TRIGGER) { stepFoot = 'right'; firstStepMs = t - startAt; dirX = Math.sign(f.ankleR.x - startAnkleR.x); }
      }
      const stand = typeof standHipY === 'function' ? standHipY() : null;
      if (stand !== null) jumpRise = Math.max(jumpRise, (stand - f.hipY) / f.scale);

      if (t - startAt >= V.MAX_MS) return settle(t);
      if (smooth < V.STOP_SPEED) { stopSince ??= t; if (t - stopSince >= V.STOP_MS) return settle(t); } else { stopSince = null; }
      return null;
    },
  };
}

/* -------------------------------------------------------------- foot trails
 * Small helpers the three foot detectors below share. A trail is the last
 * few hundred milliseconds of one point, for "how far has it moved lately";
 * a turn counter watches one coordinate for reversals.
 */
function createTrail(windowMs) {
  const ring = [];
  return {
    push(t, x, y = 0, z = 0) {
      ring.push({ t, x, y, z });
      while (ring.length && t - ring[0].t > windowMs) ring.shift();
    },
    /** When the newest point was pushed, or null. */
    get last() { return ring.length ? ring[ring.length - 1].t : null; },
    /** How much time the trail covers. Short right after a reset, so callers wait for it. */
    get span() { return ring.length ? ring[ring.length - 1].t - ring[0].t : 0; },
    /** Straight line from the oldest point in the window to the newest. Jitter cancels out. */
    get shift() {
      if (ring.length < 2) return 0;
      const a = ring[0], b = ring[ring.length - 1];
      return Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
    },
    /**
     * Every move added up, over the whole window or only since `sinceT`. A
     * foot swinging out and back keeps this high where `shift` would not.
     */
    path(sinceT = -Infinity) {
      let s = 0;
      for (let i = 1; i < ring.length; i++) {
        if (ring[i].t < sinceT) continue;
        s += Math.hypot(ring[i].x - ring[i - 1].x, ring[i].y - ring[i - 1].y, ring[i].z - ring[i - 1].z);
      }
      return s;
    },
    reset() { ring.length = 0; },
  };
}

/**
 * Reversals in one coordinate: the sign of its travel over the last `ms`,
 * ignoring travel under `dead` (in whatever units the caller pushes), and a
 * change is that sign flipping. Standing still is not a reversal and neither
 * is jitter, which is what the dead zone is for.
 */
function createTurnCounter({ ms, dead }) {
  const ring = [];
  let sign = 0, changes = 0;
  return {
    get changes() { return changes; },
    reset() { ring.length = 0; sign = 0; changes = 0; },
    push(t, v) {
      ring.push({ t, v });
      while (ring.length && t - ring[0].t > ms) ring.shift();
      const d = v - ring[0].v;
      if (Math.abs(d) < dead) return;
      const s = Math.sign(d);
      if (sign && s !== sign) changes++;
      sign = s;
    },
  };
}

/** Angle of the shoulder line in the picture, in degrees. */
function shoulderLineDeg(lm) {
  const ls = lm[LM.L_SHOULDER], rs = lm[LM.R_SHOULDER];
  return (Math.atan2(rs.y - ls.y, rs.x - ls.x) * 180) / Math.PI;
}
/**
 * Which way the shoulders face. From the world points it is the true turn
 * about the vertical; from the picture it is the shoulder line's tilt, which
 * a camera square on to the player sees shorten far more than it sees turn.
 */
function shoulderYaw(lm, W) {
  if (!W) return shoulderLineDeg(lm);
  const ls = W[LM.L_SHOULDER], rs = W[LM.R_SHOULDER];
  return (Math.atan2(rs.z - ls.z, rs.x - ls.x) * 180) / Math.PI;
}
/** Smallest turn between two angles in degrees, 0 to 180. */
const angleGap = (a, b) => Math.abs(((b - a + 540) % 360) - 180);

/* -------------------------------------------------------------------- pivot
 * One foot planted while the other swings around it. What stays on the floor
 * in a legal pivot is the ball of the foot: the heel comes up and the ankle
 * swings a little every time a foot turns on its ball, so the plant is judged
 * at the foot index landmark when the camera sees it and at the ankle when
 * it does not, and how still a foot is means how still its stillest point
 * is, ankle, heel or toe. Which foot is planted is settled by the arming
 * frames voting, not by one frame, and `confidence` says how far they agreed
 * and how well that foot was seen.
 *
 * Where the feet are and how far the plant has shifted are read from the
 * picture: the world points have the hips at their origin, so a body that
 * hops sideways does not move in them at all. What the world points are good
 * for is the free foot's path, which a sweep toward the phone hides in the
 * picture and shows in metres, and the turn, which the picture only sees as
 * the shoulders getting narrower. `plantMoved` says the plant point sat past
 * PLANT_EXIT for longer than one frame, nothing more: a travel is a referee's
 * call, never this detector's.
 */
export function createPivotDetector(T = TUNABLES) {
  const P = T.PIVOT;
  const gate = createGate(T);
  const KEYS = ['ankle', 'heel', 'toe'];
  const trailsFor = () => ({ ankle: createTrail(P.WINDOW_MS), heel: createTrail(P.WINDOW_MS), toe: createTrail(P.WINDOW_MS) });
  const trails = { left: trailsFor(), right: trailsFor() };          // picture, per point
  const swings = { left: createTrail(P.WINDOW_MS), right: createTrail(P.WINDOW_MS) }; // ankle path, world when there is one
  let armedAt = null, endAt = 0, active = false, overSince = null, lastAt = 0, last = null;
  let votes = { left: 0, right: 0 }, plant = null, anchor = null, agree = 0, prevFree = null;
  let startYaw = 0, endYaw = 0, scale = 1, unit = 1, space = 'picture';
  let drift = 0, recent = [], plantMoved = false, travel = 0, offsets = [], confs = [], footVis = [], frames = 0;

  const clear = () => {
    armedAt = null; active = false; overSince = null;
    votes = { left: 0, right: 0 }; plant = null; anchor = null; agree = 0; prevFree = null;
    drift = 0; recent = []; plantMoved = false; travel = 0; offsets = []; confs = []; footVis = []; frames = 0;
  };

  /** The point a foot pivots on: the toe when the camera sees it, else the ankle. */
  function plantPoint(foot, f, side) {
    if (foot.toe) return { x: foot.toe.x, y: foot.toe.y, point: 'toe' };
    const a = side === 'left' ? f.ankleL : f.ankleR;
    return { x: a.x, y: a.y, point: 'ankle' };
  }
  /** Where a foot is for its swing: the world ankle when there is one, else the picture ankle. */
  const freePoint = (f, W, side) => (W ? W[side === 'left' ? LM.L_ANKLE : LM.R_ANKLE] : (side === 'left' ? f.ankleL : f.ankleR));
  /** How far a foot has shifted lately: its stillest seen point, in torso lengths. */
  // Only trails fed this frame count. A heel or toe that stopped being seen
  // keeps its last window, frozen, and would read as perfectly still.
  function stillness(side, t) {
    let best = Infinity;
    for (const k of KEYS) { const tr = trails[side][k]; if (tr.span > 0 && tr.last === t) best = Math.min(best, tr.shift); }
    return best / scale;
  }
  const footSeen = (foot) => { const pts = KEYS.map((k) => foot[k]).filter(Boolean); return pts.length ? mean(pts.map(visOf)) : 0; };

  function build(holdMs) {
    const driftT = drift / scale, travelT = travel / unit;
    const scores = {
      still: ramp(driftT, P.STILL_BEST, P.STILL_ZERO),
      turn: ramp(travelT, P.TRAVEL_BEST, P.TRAVEL_ZERO),
      balance: offsets.length ? ramp(mean(offsets), P.BALANCE_BEST, P.BALANCE_ZERO) : null,
    };
    return {
      kind: 'pivot', at: endAt, source: 'measured', calibrated: Boolean(T.CALIBRATED),
      metrics: {
        plantFoot: plant,
        plantPoint: anchor.point,
        holdMs: Math.round(holdMs),
        plantDrift: +driftT.toFixed(3),
        plantMoved,
        freeTravel: +travelT.toFixed(3),
        hipTurnDeg: Math.round(angleGap(startYaw, endYaw)),
        // How sure the plant foot is: the share of arming frames that agreed
        // on it, times how well the camera saw that foot.
        confidence: +(agree * mean(footVis)).toFixed(2),
        space,
        frames, conf: +mean(confs).toFixed(2),
      },
      scores,
      overall: weighted(scores, P.WEIGHTS),
    };
  }

  /** The hold runs to the last frame the shape held, not to the frame the exit was confirmed. */
  function settle() {
    const holdMs = endAt - armedAt;
    const rep = holdMs >= P.MIN_HOLD_MS && frames > 0 ? build(holdMs) : null;
    clear();
    return rep;
  }

  return {
    get last() { return last; },
    get active() { return active; },
    reset() {
      for (const s of ['left', 'right']) { for (const k of KEYS) trails[s][k].reset(); swings[s].reset(); }
      clear(); gate.reset();
    },
    flush() { return active ? settle() : null; },

    push(lm, t) {
      const f = poseFrame(lm, T);
      const verdict = gate.judge(seesLegs(f, T), t);
      if (verdict !== 'read') {
        last = { t, skip: f.ok ? 'no-legs' : f.reason, active };
        if (verdict === 'lost') { if (active) return settle(); clear(); }
        return null;
      }
      lastAt = t;
      scale = f.scale;
      const W = worldOf(lm);
      const feet = { left: footOf(lm, 'left', T), right: footOf(lm, 'right', T) };
      for (const side of ['left', 'right']) {
        for (const k of KEYS) { const p = feet[side][k]; if (p) trails[side][k].push(t, p.x, p.y); }
        const fp = freePoint(f, W, side);
        swings[side].push(t, fp.x, fp.y, W ? fp.z : 0);
      }

      // Enough history to judge, then: the stiller foot is the candidate
      // plant, and the other one has to be clearly swinging.
      const ready = trails.left.ankle.span >= P.WINDOW_MS * 0.6;
      const stillL = stillness('left', t), stillR = stillness('right', t);
      const cand = stillL <= stillR ? 'left' : 'right';
      const stillest = Math.min(stillL, stillR);
      const swing = swings[cand === 'left' ? 'right' : 'left'].path() / f.bodyScale;
      const shape = ready && stillest <= P.STILL_MAX && swing >= P.FREE_MIN;
      last = { t, plant: active ? plant : (shape ? cand : null), still: +stillest.toFixed(3), swing: +swing.toFixed(3), active, shape };

      if (!active) {
        if (!shape) { armedAt = null; return null; }
        if (armedAt === null) { armedAt = t; votes = { left: 0, right: 0 }; }
        votes[cand]++;
        if (t - armedAt < P.ARM_MS) return null;
        // The arming frames vote on the plant foot. Split votes are two feet
        // trading places, not a pivot: forget it and wait for a clear one.
        agree = Math.max(votes.left, votes.right) / (votes.left + votes.right);
        if (agree < P.MIN_AGREE) { armedAt = null; return null; }
        plant = votes.left >= votes.right ? 'left' : 'right';
        const free = plant === 'left' ? 'right' : 'left';
        unit = W ? f.bodyScale : scale; space = W ? 'world' : 'picture';
        const a = plant === 'left' ? f.ankleL : f.ankleR;
        anchor = { ...plantPoint(feet[plant], f, plant), ankle: { x: a.x, y: a.y } };
        startYaw = shoulderYaw(lm, W); endYaw = startYaw;
        // The swing counts from the frame the pivot armed, as the hold does.
        travel = swings[free].path(armedAt);
        prevFree = freePoint(f, W, free);
        active = true; overSince = null; endAt = t;
      }

      // Inside a pivot: how far the plant point has shifted from where it was
      // set, how far the free foot has swung, and where the hips sit. If the
      // toe blinks out mid hold the drift is measured ankle to ankle instead,
      // so the number never jumps between two different points.
      const free = plant === 'left' ? 'right' : 'left';
      const p = plantPoint(feet[plant], f, plant);
      const plantAnkle = plant === 'left' ? f.ankleL : f.ankleR;
      const d = p.point === anchor.point
        ? Math.hypot(p.x - anchor.x, p.y - anchor.y)
        : Math.hypot(plantAnkle.x - anchor.ankle.x, plantAnkle.y - anchor.ankle.y);
      // What counts is the shift the plant point held for MOVE_MS, the least
      // of its last MOVE_MS of readings: a landmark that jumps for one frame
      // and comes back neither moves the drift nor the verdict.
      recent.push({ t, d });
      // MOVE_MS or two frames, whichever is longer: below 12.5 fps one frame
      // spans the window, and a dragged plant foot would never read as moved.
      while (recent.length > 2 && t - recent[0].t > P.MOVE_MS) recent.shift();
      if (recent.length >= 2) {
        const held = Math.min(...recent.map((r) => r.d));
        drift = Math.max(drift, held);
        if (held / scale > P.PLANT_EXIT) plantMoved = true;
      }
      const fp = freePoint(f, W, free);
      if (prevFree) travel += dist3(fp, prevFree);
      prevFree = fp;
      offsets.push(Math.abs(f.hipX - p.x) / scale);
      confs.push(f.conf); footVis.push(footSeen(feet[plant])); frames++;
      endYaw = shoulderYaw(lm, W);

      const over = plantMoved || swings[free].path(t - P.STOP_MS) / f.bodyScale < P.FREE_STOP;
      if (over) { overSince ??= t; if (t - overSince >= P.EXIT_MS) return settle(); } else { overSince = null; endAt = t; }
      return null;
    },
  };
}

/* -------------------------------------------------------------------- slide
 * A defensive slide: low, wide, moving sideways, feet never crossing. The
 * sideways part is movement across the picture, so it is honest with the
 * phone in front of or behind the player and blind to a slide straight at
 * it. How low and how wide come from the world points when there are any,
 * so a phone on the floor reads the same knees as one on a chair. Left and
 * right are frame order, not the player's: which ankle starts on which side
 * is taken from the first frame, and a crossing is that order flipping.
 * `endedBy` says what stopped it: the player stood up, stopped moving, kept
 * the feet together, was lost, or just went on past MAX_MS.
 */
export function createSlideDetector(T = TUNABLES) {
  const L = T.SLIDE;
  const gate = createGate(T);
  const lateral = createTrail(L.WINDOW_MS);
  const turns = createTurnCounter({ ms: L.DIR_MS, dead: L.DIR_DEAD });
  let armedAt = null, endAt = 0, active = false, overSince = null, overBy = null, narrowSince = null, last = null;
  let order = 0, crossings = 0, distance = 0, prevX = null, scale = 1;
  let knees = [], widths = [], confs = [], frames = 0;

  const clear = () => {
    armedAt = null; active = false; overSince = null; overBy = null; narrowSince = null;
    order = 0; crossings = 0; distance = 0; prevX = null;
    knees = []; widths = []; confs = []; frames = 0; turns.reset();
  };

  function build(durationMs, endedBy) {
    const kneeMean = mean(knees), widthRatio = mean(widths);
    const speed = (distance / scale) / (Math.max(durationMs, 1) / 1000);
    const scores = {
      low: ramp(kneeMean, L.LOW_BEST, L.LOW_ZERO),
      wide: plateau(widthRatio, L.WIDE_LO, L.WIDE_HI, L.WIDE_PAD),
      noCross: Math.max(0, 100 - crossings * L.CROSS_PENALTY),
      quick: ramp(speed, L.QUICK_BEST, L.QUICK_ZERO),
    };
    return {
      kind: 'slide', at: endAt, source: 'measured', calibrated: Boolean(T.CALIBRATED),
      metrics: {
        durationMs: Math.round(durationMs),
        distance: +distance.toFixed(3),
        lateralSpeed: +speed.toFixed(2),
        crossings,
        kneeMean: Math.round(kneeMean),
        widthRatio: +widthRatio.toFixed(2),
        directionChanges: turns.changes,
        endedBy,
        frames, conf: +mean(confs).toFixed(2),
      },
      scores,
      overall: weighted(scores, L.WEIGHTS),
    };
  }

  /** The slide runs to the last frame it was still one, not to the frame the exit was confirmed. */
  function settle(endedBy) {
    const durationMs = endAt - armedAt;
    const rep = durationMs >= L.MIN_MS && frames > 0 ? build(durationMs, endedBy) : null;
    clear();
    return rep;
  }

  return {
    get last() { return last; },
    get active() { return active; },
    reset() { lateral.reset(); clear(); gate.reset(); },
    flush() { return active ? settle('flush') : null; },

    push(lm, t) {
      const f = poseFrame(lm, T);
      const verdict = gate.judge(seesLegs(f, T), t);
      if (verdict !== 'read') {
        last = { t, skip: f.ok ? 'no-legs' : f.reason, active };
        if (verdict === 'lost') { if (active) return settle('lost'); clear(); }
        return null;
      }
      scale = f.scale;
      lateral.push(t, f.ankleMid.x);
      const span = lateral.span;
      const speed = span >= L.WINDOW_MS * 0.6 ? (lateral.path() / scale) / (span / 1000) : 0;
      const low = f.kneeMean <= L.KNEE_ENTER;
      const wide = f.ankleSpread >= L.WIDTH_ENTER;
      const shape = low && wide && speed >= L.MOVE_START;
      last = { t, knee: Math.round(f.kneeMean), width: +f.ankleSpread.toFixed(2), speed: +speed.toFixed(2), active, shape };

      if (!active) {
        if (shape) {
          if (armedAt === null) { armedAt = t; prevX = f.ankleMid.x; order = Math.sign(f.ankleR.x - f.ankleL.x); distance = 0; }
          // Ground covered counts from the frame the slide armed, as the duration does.
          distance += Math.abs(f.ankleMid.x - prevX); prevX = f.ankleMid.x;
        } else { armedAt = null; distance = 0; }
        if (armedAt !== null && t - armedAt >= L.ARM_MS) { active = true; overSince = null; narrowSince = null; endAt = t; turns.reset(); }
        else return null;
      }

      // Standing up or stopping ends it, once it has lasted EXIT_MS. Those
      // frames are not collected: standing up at the end would otherwise pull
      // the knee average up and, with the feet back in their usual order,
      // count as a crossing.
      const stoodUp = f.kneeMean > L.KNEE_EXIT, stopped = speed < L.MOVE_STOP;
      if (stoodUp || stopped) {
        if (overSince === null) { overSince = t; overBy = stoodUp ? 'stood-up' : 'stopped'; }
        if (t - overSince >= L.EXIT_MS) return settle(overBy);
        return null;
      }
      overSince = null; endAt = t;

      knees.push(f.kneeMean); widths.push(f.ankleSpread); confs.push(f.conf); frames++;
      distance += Math.abs(f.ankleMid.x - prevX); prevX = f.ankleMid.x;
      turns.push(t, f.ankleMid.x / scale);
      // Which ankle is on which side of the picture. A flip is a crossing;
      // ankles too close to put in order are left as they were.
      const gap = (f.ankleR.x - f.ankleL.x) / scale;
      if (Math.abs(gap) >= L.CROSS_DEAD) {
        const cur = Math.sign(gap);
        if (order && cur !== order) crossings++;
        order = cur;
      }

      if (t - armedAt >= L.MAX_MS) return settle('max');
      // Feet together for a moment is a cross step. Held there, the slide is over.
      if (f.ankleSpread < L.WIDTH_ENTER * L.WIDTH_EXIT) { narrowSince ??= t; if (t - narrowSince >= L.NARROW_MS) return settle('narrow'); }
      else narrowSince = null;
      return null;
    },
  };
}

/* ----------------------------------------------------------------- footwork
 * Steps. A foot is up when its sole, the heel and toe the camera sees, or
 * the ankle when it sees neither, sits LIFT torso lengths higher than the
 * other foot's, and it has stepped when it comes back down within LAND. The
 * other foot is the floor: a jump lifts both, reads as no step, and that is
 * on purpose. From the world points that height is real height; from the
 * picture a foot further from a low phone sits higher too, so a step toward
 * the camera is a rough count, and a screen should say steps, never
 * distance. Step length is read from the picture, where a step actually
 * moves the foot. Ground contact time is left out on purpose: with this
 * signal it is the flip side of cadence, and a score that repeats another
 * one is not a second measurement.
 */
export function createFootworkDetector(T = TUNABLES) {
  const W = T.FOOTWORK;
  const gate = createGate(T);
  const turns = createTurnCounter({ ms: W.DIR_MS, dead: W.DIR_DEAD });
  let active = false, startAt = 0, lastStepAt = 0, lastAt = 0, last = null;
  let up = null, lastSide = null;   // the foot in the air right now, and the last one to land
  let steps = 0, alternations = 0, lens = [], airs = [], rises = [], offsets = [], confs = [], frames = 0;

  const clear = () => {
    active = false; up = null; lastSide = null;
    steps = 0; alternations = 0; lens = []; airs = []; rises = []; offsets = []; confs = []; frames = 0; turns.reset();
  };

  /**
   * Each foot's height above the lower one, in torso lengths, world when there
   * is one, measured two ways: at the soles (the lower of heel and toe, both
   * seen) and at the ankles. `ref` says which one this frame can use: both
   * feet are judged at the same point, so with either sole unseen it is
   * ankle against ankle.
   */
  function heights(lm, f) {
    const Wp = worldOf(lm), src = Wp || lm;
    // A sole is heel AND toe. A heel alone is a different point under the same
    // name, and a toe blinking out made a heel raise read as a lift.
    const sole = (side) => {
      const foot = footOf(src, side, T);
      return foot.heel && foot.toe ? Math.max(foot.heel.y, foot.toe.y) : null;
    };
    const pair = (l, r) => { const floor = Math.max(l, r); return { left: (floor - l) / f.bodyScale, right: (floor - r) / f.bodyScale }; };
    const ankle = pair(src[LM.L_ANKLE].y, src[LM.R_ANKLE].y);
    const l = sole('left'), r = sole('right');
    return l === null || r === null ? { ...ankle, ref: 'ankle', ankle, sole: null } : { ...pair(l, r), ref: 'sole', ankle, sole: pair(l, r) };
  }

  function build() {
    const durationMs = lastStepAt - startAt;
    const stepsPerSec = durationMs > 0 ? steps / (durationMs / 1000) : null;
    const scores = {
      quick: stepsPerSec === null ? null : ramp(stepsPerSec, W.QUICK_BEST, W.QUICK_ZERO),
      balance: offsets.length ? ramp(mean(offsets), W.BALANCE_BEST, W.BALANCE_ZERO) : null,
    };
    return {
      kind: 'footwork', at: lastAt, source: 'measured', calibrated: Boolean(T.CALIBRATED),
      metrics: {
        steps,
        stepsPerSec: stepsPerSec === null ? null : +stepsPerSec.toFixed(2),
        avgStepLen: +mean(lens).toFixed(3),
        avgAirMs: Math.round(mean(airs)),
        avgRise: +mean(rises).toFixed(2),
        // How many steps landed on the other foot from the one before.
        alternatedPct: steps > 1 ? Math.round((alternations / (steps - 1)) * 100) : null,
        directionChanges: turns.changes,
        durationMs: Math.round(durationMs),
        frames, conf: +mean(confs).toFixed(2),
      },
      scores,
      overall: weighted(scores, W.WEIGHTS),
    };
  }

  function settle() {
    const rep = steps >= W.MIN_STEPS ? build() : null;
    clear();
    return rep;
  }

  return {
    get last() { return last; },
    get active() { return active; },
    reset() { clear(); gate.reset(); },
    flush() { return active ? settle() : null; },

    push(lm, t) {
      const f = poseFrame(lm, T);
      const verdict = gate.judge(seesLegs(f, T), t);
      if (verdict !== 'read') {
        last = { t, skip: f.ok ? 'no-legs' : f.reason, active };
        if (verdict === 'lost') { up = null; if (active) return settle(); }
        return null;
      }
      lastAt = t;
      const upright = f.kneeMean >= W.KNEE_MIN && Math.abs(f.trunkLean) <= W.LEAN_MAX;
      const h = heights(lm, f);
      let landed = null;
      if (!up) {
        if (upright && h.left >= W.LIFT) up = { side: 'left', at: t, x: f.ankleL.x, y: f.ankleL.y, peak: h.left, ref: h.ref };
        else if (upright && h.right >= W.LIFT) up = { side: 'right', at: t, x: f.ankleR.x, y: f.ankleR.y, peak: h.right, ref: h.ref };
      } else if (t - up.at > W.MAX_AIR_MS) {
        up = null;                                   // held up, not a step
      } else if (up.ref === 'ankle' || h.sole) {
        // A step is judged to its landing on the point it left the floor on.
        // Switching mid step, when a toe blinks in and out of view, read one
        // heel raise as a run of lifts and landings: 24 steps out of 6.
        const rise = (up.ref === 'sole' ? h.sole : h.ankle)[up.side];
        up.peak = Math.max(up.peak, rise);
        if (rise <= W.LAND) {
          const a = up.side === 'left' ? f.ankleL : f.ankleR;
          landed = { side: up.side, liftAt: up.at, len: Math.hypot(a.x - up.x, a.y - up.y), airMs: t - up.at, peak: up.peak };
          up = null;
        }
      }
      last = { t, rise: { left: +h.left.toFixed(2), right: +h.right.toFixed(2) }, up: up ? up.side : null, steps, active };

      if (landed) {
        if (!active) { active = true; startAt = landed.liftAt; turns.reset(); lastSide = null; }
        if (lastSide && lastSide !== landed.side) alternations++;
        lastSide = landed.side;
        steps++; lastStepAt = t; lens.push(landed.len); airs.push(landed.airMs); rises.push(landed.peak);
      }
      if (!active) return null;

      offsets.push(Math.abs(f.hipX - f.ankleMid.x) / f.scale);
      confs.push(f.conf); frames++;
      turns.push(t, f.ankleMid.x / f.scale);

      if (t - startAt >= W.MAX_MS) return settle();
      if (t - lastStepAt >= W.GAP_MS) return settle();
      return null;
    },
  };
}

/* ------------------------------------------------------------------ fatigue
 * The honest one, and the one worth the most.
 *
 * Nothing here is a verdict about a player's fitness and nothing here is
 * medical. It is one comparison: the player's own last few reps against the
 * player's own first few reps, in the same session, on signals a camera can
 * actually measure. Release height, stance depth, a jump proxy, and how long
 * they are resting between reps.
 *
 * A screen showing this says what it is: "your last five compared with your
 * first five, today". It never says tired, unfit, or anything about health.
 */
export const FATIGUE_SIGNALS = {
  // tiring: the direction of drift that usually shows up as a session goes on.
  releaseHeight: { tiring: 'down', unit: 'shoulder widths' },
  stanceDepth: { tiring: 'down', unit: 'degrees of bend' },
  jump: { tiring: 'down', unit: 'torso lengths' },
  restGapMs: { tiring: 'up', unit: 'ms between reps' },
};

export function createFatigueTracker(T = TUNABLES) {
  const F = T.FATIGUE;
  const values = { releaseHeight: [], stanceDepth: [], jump: [], restGapMs: [] };
  let firstAt = null, lastEffortAt = null, reps = 0;

  function add(signal, value, t) {
    if (!(signal in values)) return;
    if (!Number.isFinite(value)) return;
    values[signal].push({ t, v: value });
    if (values[signal].length > 400) values[signal].shift();
  }

  function band(driftPct) {
    const d = Math.abs(driftPct);
    if (d < F.NOISE_PCT) return 'noise';
    if (d < F.NOTICEABLE_PCT) return 'small';
    if (d < F.LARGE_PCT) return 'noticeable';
    return 'large';
  }

  function drift(signal) {
    const list = values[signal];
    const need = F.BASELINE_REPS + F.RECENT_REPS;
    if (list.length < need) return { n: list.length, need, ready: false };
    const base = mean(list.slice(0, F.BASELINE_REPS).map((r) => r.v));
    const recent = mean(list.slice(-F.RECENT_REPS).map((r) => r.v));
    if (!Number.isFinite(base) || Math.abs(base) < 1e-6) return { n: list.length, need, ready: false };
    const driftPct = ((recent - base) / Math.abs(base)) * 100;
    const dir = driftPct > 0 ? 'up' : driftPct < 0 ? 'down' : 'flat';
    return {
      ready: true, n: list.length, need,
      baseline: +base.toFixed(3), recent: +recent.toFixed(3),
      driftPct: +driftPct.toFixed(1),
      dir, tiring: dir === FATIGUE_SIGNALS[signal].tiring && band(driftPct) !== 'noise',
      band: band(driftPct),
    };
  }

  return {
    reset() {
      for (const k of Object.keys(values)) values[k] = [];
      firstAt = null; lastEffortAt = null; reps = 0;
    },

    /** Feed it every measured rep. It picks out the signals it can use. */
    note(rep) {
      if (!rep || rep.source !== 'measured') return;
      const t = rep.at;
      if (firstAt === null) firstAt = t;
      reps++;
      const m = rep.metrics || {};
      if (rep.kind === 'shot') {
        add('releaseHeight', m.releaseRise, t);
        if (Number.isFinite(m.jumpRise)) add('jump', m.jumpRise, t);
      }
      if (rep.kind === 'stance' && Number.isFinite(m.kneeMean)) add('stanceDepth', 180 - m.kneeMean, t);
      if (rep.kind === 'drive' && Number.isFinite(m.jumpRise)) add('jump', m.jumpRise, t);
      // Rest between efforts. Shots and drives only: a dribble is not a rep.
      if (rep.kind === 'shot' || rep.kind === 'drive') {
        if (lastEffortAt !== null) {
          const gap = t - lastEffortAt;
          if (gap > 0 && gap < 600000) add('restGapMs', gap, t);
        }
        lastEffortAt = t;
      }
    },

    report(nowMs = null) {
      const endAt = nowMs === null ? (lastEffortAt ?? firstAt) : nowMs;
      const sessionMs = firstAt === null ? 0 : Math.max(0, endAt - firstAt);
      const signals = {};
      for (const key of Object.keys(values)) signals[key] = drift(key);
      const usable = Object.entries(signals).filter(([, s]) => s.ready);
      const longEnough = sessionMs >= F.MIN_SESSION_MS;
      // The headline is the biggest real move in the tiring direction. If
      // nothing moved in that direction there is no headline, on purpose.
      let headline = null;
      for (const [key, s] of usable) {
        if (!s.tiring) continue;
        if (!headline || Math.abs(s.driftPct) > Math.abs(headline.driftPct)) headline = { key, ...s };
      }
      return {
        ready: longEnough && usable.length > 0,
        reps, sessionMs: Math.round(sessionMs),
        need: { reps: F.BASELINE_REPS + F.RECENT_REPS, sessionMs: F.MIN_SESSION_MS },
        longEnough,
        signals, headline,
        compare: { baselineReps: F.BASELINE_REPS, recentReps: F.RECENT_REPS },
      };
    },
  };
}

/* -------------------------------------------------------------- calibration
 * None of the thresholds in TUNABLES has ever been measured against a real
 * player. Calibration is how a score stops being a guess: the player does a
 * few reps, the app records THEIR range, and every score that would otherwise
 * have leaned on a made up number is expressed against that range instead.
 *
 * What calibration does NOT do is move a target that physics already sets.
 * The elbow one is the example: the elbow wants to be under the wrist, and
 * personalising that to a player's habit would score a bad habit as good. So
 * geometry stays fixed and range, pace, depth and hold get personalised.
 */
export const CALIBRATION_STEPS = [
  {
    id: 'shots', kind: 'shot', need: TUNABLES.CALIBRATION.SHOTS_NEEDED,
    title: 'Ten shots',
    what: 'Shoot ten the way you normally shoot, and mark each one made or missed.',
    gives: 'Your own release angle on the ones you made, and how long you hold your follow through.',
  },
  {
    id: 'stance', kind: 'stance', need: TUNABLES.CALIBRATION.STANCE_NEEDED,
    title: 'Three stances',
    what: 'Get in a defensive stance and hold it about three seconds. Three times.',
    gives: 'How deep your stance actually gets, so depth is scored against you and not a guess.',
  },
  {
    id: 'dribble', kind: 'dribble', need: TUNABLES.CALIBRATION.DRIBBLE_NEEDED,
    title: 'Twenty dribbles',
    what: 'Dribble on the spot at your normal speed, both hands.',
    gives: 'Your own pace, so rhythm is measured against how you dribble.',
  },
  {
    id: 'drive', kind: 'drive', need: TUNABLES.CALIBRATION.JUMP_NEEDED,
    title: 'Three first steps',
    what: 'Three first steps out of a stance. Left, right, left.',
    gives: 'Your own burst and step length, and the jump height everything later is compared with.',
  },
];

/** The shape of a set of numbers: what the player's own range actually is. */
export function bandOf(values) {
  const list = (values || []).filter((v) => Number.isFinite(v));
  if (!list.length) return { n: 0 };
  const r3 = (v) => +v.toFixed(3);
  return {
    n: list.length,
    min: r3(Math.min(...list)), max: r3(Math.max(...list)),
    p10: r3(percentile(list, 0.1)), p25: r3(percentile(list, 0.25)),
    p50: r3(percentile(list, 0.5)),
    p75: r3(percentile(list, 0.75)), p90: r3(percentile(list, 0.9)),
    mean: r3(mean(list)), sd: r3(stdev(list)),
  };
}

/**
 * Score one value against the player's own range. 50 is their own middle,
 * 100 is their own best rep, 0 is their own worst. It is a comparison with
 * themselves and it says nothing about anybody else.
 */
export function relScore(value, band, higherIsBetter = true) {
  if (!band || !band.n || !Number.isFinite(value)) return null;
  const best = higherIsBetter ? band.p90 : band.p10;
  const worst = higherIsBetter ? band.p10 : band.p90;
  const mid = band.p50;
  if (best === worst) return 50;
  const towardBest = higherIsBetter ? value >= mid : value <= mid;
  if (towardBest) {
    if (best === mid) return 100;
    return round(clamp(50 + (50 * (value - mid)) / (best - mid), 50, 100));
  }
  if (mid === worst) return 0;
  return round(clamp((50 * (value - worst)) / (mid - worst), 0, 50));
}

/**
 * Turn a pile of calibration reps into the player's baseline. `reps` is the
 * flat list the detectors produced; shot reps may carry made: true/false,
 * which is the only thing here that comes from a human rather than the camera.
 */
export function buildBaseline(reps, { at = Date.now(), T = TUNABLES } = {}) {
  const C = T.CALIBRATION;
  const list = Array.isArray(reps) ? reps : [];
  const of = (kind) => list.filter((r) => r && r.kind === kind && r.metrics);
  const shots = of('shot'), stances = of('stance'), dribbles = of('dribble'), drives = of('drive');
  const made = shots.filter((r) => r.made === true);
  const jumps = drives.map((r) => r.metrics.jumpRise).filter((v) => Number.isFinite(v));

  const baseline = {
    v: 1, at,
    counts: {
      shots: shots.length, made: made.length, stance: stances.length,
      dribble: dribbles.length, drive: drives.length, jump: jumps.length,
    },
    shot: {
      releaseAngle: bandOf(shots.map((r) => r.metrics.releaseAngle)),
      releaseAngleMade: bandOf(made.map((r) => r.metrics.releaseAngle)),
      releaseRise: bandOf(shots.map((r) => r.metrics.releaseRise)),
      followFrames: bandOf(shots.map((r) => r.metrics.followFrames)),
      followMs: bandOf(shots.map((r) => r.metrics.followMs)),
      elbowOffset: bandOf(shots.map((r) => Math.abs(r.metrics.elbowOffset))),
    },
    stance: {
      kneeMean: bandOf(stances.map((r) => r.metrics.kneeMean)),
      widthRatio: bandOf(stances.map((r) => r.metrics.widthRatio)),
      holdMs: bandOf(stances.map((r) => r.metrics.holdMs)),
    },
    dribble: {
      intervalMs: bandOf(dribbles.map((r) => r.metrics.intervalMs)),
      lowHeight: bandOf(dribbles.map((r) => r.metrics.lowHeight)),
    },
    drive: {
      peakSpeed: bandOf(drives.map((r) => r.metrics.peakSpeed)),
      firstStepLen: bandOf(drives.map((r) => r.metrics.firstStepLen)),
      kneePush: bandOf(drives.map((r) => r.metrics.kneePush)),
      jumpRise: bandOf(jumps),
    },
  };
  baseline.ready = {
    shots: shots.length >= C.SHOTS_NEEDED,
    release: made.length >= C.MADE_NEEDED,
    stance: stances.length >= C.STANCE_NEEDED,
    dribble: dribbles.length >= C.DRIBBLE_NEEDED,
    drive: drives.length >= C.JUMP_NEEDED,
  };
  return baseline;
}

/** Is this baseline worth anything, and is it still recent enough? */
export function baselineStatus(baseline, { now = Date.now(), T = TUNABLES } = {}) {
  const parts = baseline?.ready || {};
  const any = Object.values(parts).some(Boolean);
  const ageDays = baseline?.at ? Math.max(0, Math.round((now - baseline.at) / 864e5)) : null;
  return {
    calibrated: Boolean(baseline) && any,
    parts,
    ageDays,
    stale: ageDays !== null && ageDays > T.CALIBRATION.MAX_AGE_DAYS,
    counts: baseline?.counts || null,
  };
}

/**
 * A copy of TUNABLES with the player's own numbers in it. Anything they have
 * not earned yet keeps the original guess, and CALIBRATED records exactly
 * which parts are real so a screen can be honest field by field.
 */
export function calibratedTunables(T = TUNABLES, baseline = null) {
  const out = JSON.parse(JSON.stringify(T));
  if (!baseline) return out;
  const C = out.CALIBRATION;
  const parts = { release: false, follow: false, dribble: false, stance: false, drive: false };

  const madeBand = baseline.shot?.releaseAngleMade;
  if (madeBand?.n >= C.MADE_NEEDED) {
    // Their own release on the shots they made. Not a coaching ideal, and it
    // is only as good as the made and missed taps underneath it.
    out.RELEASE_ANGLE_IDEAL = Math.round(madeBand.p50);
    out.RELEASE_ANGLE_ZERO_AT = Math.round(clamp((madeBand.p90 - madeBand.p10) * 2.5, C.RELEASE_SPREAD_MIN, C.RELEASE_SPREAD_MAX));
    parts.release = true;
  }
  // Their own longer holds, in time, so the target is the same on every
  // phone. A baseline saved before followMs existed holds frames at 30 fps.
  const followMs = baseline.shot?.followMs?.n ? baseline.shot.followMs : null;
  const followFrames = baseline.shot?.followFrames;
  const follow = followMs || (followFrames?.n ? { n: followFrames.n, p75: followFrames.p75 * (1000 / 30) } : null);
  if (follow?.n >= C.SHOTS_NEEDED && follow.p75 > 0) {
    out.FOLLOW_MS_IDEAL = Math.round(clamp(follow.p75, C.FOLLOW_MIN_MS, C.FOLLOW_MAX_MS));
    parts.follow = true;
  }
  const interval = baseline.dribble?.intervalMs;
  if (interval?.n >= C.DRIBBLE_NEEDED && interval.p50 > 0) {
    out.DRIBBLE.PACE_IDEAL_HZ = +(1000 / interval.p50).toFixed(2);
    const spread = Math.abs(1000 / Math.max(interval.p10, 1) - 1000 / Math.max(interval.p90, 1));
    out.DRIBBLE.PACE_ZERO_AT_HZ = +clamp(spread * 1.5, 0.6, 2.5).toFixed(2);
    parts.dribble = true;
  }
  const knee = baseline.stance?.kneeMean;
  if (knee?.n >= C.STANCE_NEEDED) {
    // Depth measured against how deep this player actually gets.
    out.STANCE.DEPTH_BEST = Math.round(clamp(knee.p10, 90, 160));
    out.STANCE.DEPTH_ZERO = Math.round(clamp(knee.p90 + 12, out.STANCE.DEPTH_BEST + 10, 178));
    out.BOXOUT.SINK_BEST = out.STANCE.DEPTH_BEST;
    out.BOXOUT.SINK_ZERO = out.STANCE.DEPTH_ZERO;
    parts.stance = true;
  }
  const speed = baseline.drive?.peakSpeed, step = baseline.drive?.firstStepLen, push = baseline.drive?.kneePush;
  if (speed?.n >= C.JUMP_NEEDED) {
    out.DRIVE.BURST_BEST = +clamp(speed.p90, 0.6, 6).toFixed(2);
    out.DRIVE.BURST_ZERO = +clamp(speed.p10 * 0.6, 0.1, out.DRIVE.BURST_BEST - 0.2).toFixed(2);
    if (step?.n) {
      out.DRIVE.STEP_BEST = +clamp(step.p90, 0.2, 2.5).toFixed(2);
      out.DRIVE.STEP_ZERO = +clamp(step.p10 * 0.5, 0.02, out.DRIVE.STEP_BEST - 0.05).toFixed(2);
    }
    if (push?.n) {
      out.DRIVE.LOW_BEST = Math.round(clamp(push.p10, 90, 165));
      out.DRIVE.LOW_ZERO = Math.round(clamp(push.p90 + 10, out.DRIVE.LOW_BEST + 8, 178));
    }
    parts.drive = true;
  }

  // The elbow target is deliberately untouched: it is geometry, not taste.
  out.CALIBRATED = Object.values(parts).some(Boolean)
    ? { at: baseline.at || null, parts, fixed: ['elbow'] }
    : null;
  return out;
}

/* ------------------------------------------------------------ steady points
 * MediaPipe's landmarks shake. A player standing perfectly still has a wrist
 * that wanders a few pixels every frame, and every detector downstream reads
 * that wander as movement: a dribble that is not happening, a balance score
 * that flickers, dots that visibly buzz on the screen.
 *
 * This is the One Euro filter (Casiez, Roussel and Vogel, CHI 2012), which is
 * the standard answer for exactly this signal. It is a low pass filter whose
 * cutoff rises with speed. Standing still, the cutoff is low and the jitter is
 * smoothed hard. Mid jump shot, the cutoff opens up and the point follows the
 * hand with almost no lag, which matters because the release is scored at the
 * fastest moment of the whole motion and a laggy filter would score it wrong.
 *
 * Visibility is passed through untouched: it is a confidence, not a position.
 */
/* Chosen from a sweep, not by feel (scripts/track-test.mjs holds it there).
 * At 30 frames a second these cut the shake on a still point by about 70
 * percent and trail a fast moving hand by 0.8 of one frame. Lower BETA was
 * steadier by a point or two and nearly two frames late on the release, which
 * is the one moment the score depends on. These are measured against synthetic
 * jitter, not against a real player, and want checking on real footage. */
export const SMOOTH = {
  MIN_CUTOFF: 1.0,   // Hz. Lower is steadier at rest and laggier
  BETA: 4,           // how fast the cutoff opens with speed. Higher is snappier
  D_CUTOFF: 1.0,     // Hz, for the speed estimate itself
  // A joint the model is unsure of is smoothed harder. The cutoff, and how
  // fast it opens with speed, are scaled from VIS_GAIN of their value at
  // VIS_LOW visibility up to their full value at VIS_HIGH, so a half seen
  // ankle stops buzzing and a clearly seen wrist keeps its snap. Checked on
  // synthetic jitter only (scripts/test-pose.mjs), not on a real player.
  VIS_LOW: 0.3, VIS_HIGH: 0.8, VIS_GAIN: 0.25,
};

const alphaFor = (cutoff, dt) => { const tau = 1 / (2 * Math.PI * cutoff); return 1 / (1 + tau / dt); };

function oneEuro(P = SMOOTH) {
  let x = null, dx = 0, tPrev = null;
  return (value, tMs, vis = 1) => {
    if (x === null || tPrev === null) { x = value; tPrev = tMs; return value; }
    // A stalled tab or a dropped frame: clamp so one long gap cannot fling the point.
    const dt = Math.min(0.25, Math.max(1 / 240, (tMs - tPrev) / 1000));
    tPrev = tMs;
    const rawDx = (value - x) / dt;
    dx += alphaFor(P.D_CUTOFF, dt) * (rawDx - dx);
    const sure = clamp((vis - P.VIS_LOW) / Math.max(P.VIS_HIGH - P.VIS_LOW, 1e-6), 0, 1);
    const gain = P.VIS_GAIN + (1 - P.VIS_GAIN) * sure;
    x += alphaFor((P.MIN_CUTOFF + P.BETA * Math.abs(dx)) * gain, dt) * (value - x);
    return x;
  };
}

/** Smooths one body's landmarks over time. One of these per body, never shared. */
export function createLandmarkSmoother(P = SMOOTH) {
  let filters = null, lastAt = null;
  const fresh = () => ({ x: oneEuro(P), y: oneEuro(P), z: oneEuro(P) });
  return {
    reset() { filters = null; lastAt = null; },
    push(lm, tMs) {
      if (!Array.isArray(lm) || !lm.length) return lm;
      // After a real gap the old state describes somewhere they no longer are.
      if (lastAt !== null && tMs - lastAt > 600) filters = null;
      lastAt = tMs;
      if (!filters || filters.length !== lm.length) filters = lm.map(() => ({ ...fresh(), w: null }));
      const out = lm.map((pt, i) => {
        if (!pt || typeof pt.x !== 'number') return pt;
        const f = filters[i], v = pt.visibility;
        return { ...pt, x: f.x(pt.x, tMs, v), y: f.y(pt.y, tMs, v), z: typeof pt.z === 'number' ? f.z(pt.z, tMs, v) : pt.z };
      });
      // The world points ride along, steadied by filters of their own: they
      // are metres, not frame units, so never the picture point's filter.
      const w = worldOf(lm);
      if (w) {
        out.world = w.map((pt, i) => {
          const f = filters[i]; f.w ??= fresh();
          const v = pt.visibility;
          return { ...pt, x: f.w.x(pt.x, tMs, v), y: f.w.y(pt.y, tMs, v), z: f.w.z(pt.z, tMs, v) };
        });
      }
      return out;
    },
  };
}

/* ------------------------------------------------------------ who goes where
 * The best way to pair N bodies with N slots so the TOTAL cost is lowest. The
 * tracker used to take the closest pair first and work outward, which is fine
 * until two players cross: grabbing the single best pair first can force the
 * other pair into a swap that a whole-picture answer would have avoided. With
 * ten bodies on a court that happens constantly.
 *
 * This is the Hungarian method. cost is rows x cols; returns, for each row, the
 * column it was given, or -1. Rows and columns may differ in number.
 */
export function assignMinCost(cost) {
  const n = cost.length, m = n ? cost[0].length : 0;
  if (!n || !m) return new Array(n).fill(-1);
  // The method wants rows <= cols. Flip and flip back when that is not so.
  if (n > m) {
    const flipped = assignMinCost(cost[0].map((_, j) => cost.map((row) => row[j])));
    const out = new Array(n).fill(-1);
    flipped.forEach((r, j) => { if (r >= 0) out[r] = j; });
    return out;
  }
  const u = new Array(n + 1).fill(0), v = new Array(m + 1).fill(0);
  const pick = new Array(m + 1).fill(0), way = new Array(m + 1).fill(0);
  for (let i = 1; i <= n; i++) {
    pick[0] = i;
    let j0 = 0;
    const minv = new Array(m + 1).fill(Infinity), used = new Array(m + 1).fill(false);
    do {
      used[j0] = true;
      const i0 = pick[j0];
      let delta = Infinity, j1 = 0;
      for (let j = 1; j <= m; j++) {
        if (used[j]) continue;
        const cur = cost[i0 - 1][j - 1] - u[i0] - v[j];
        if (cur < minv[j]) { minv[j] = cur; way[j] = j0; }
        if (minv[j] < delta) { delta = minv[j]; j1 = j; }
      }
      for (let j = 0; j <= m; j++) {
        if (used[j]) { u[pick[j]] += delta; v[j] -= delta; } else minv[j] -= delta;
      }
      j0 = j1;
    } while (pick[j0] !== 0);
    do { const j1 = way[j0]; pick[j0] = pick[j1]; j0 = j1; } while (j0);
  }
  const out = new Array(n).fill(-1);
  for (let j = 1; j <= m; j++) if (pick[j] > 0) out[pick[j] - 1] = j - 1;
  return out;
}

/**
 * How different two outfits are, 0 to about 1. Null when either has nothing to
 * compare.
 *
 * THE METRIC WAS CHOSEN BY MEASUREMENT. The first version compared
 * chromaticity, r/(r+g+b), which divides by brightness and so explodes on dark
 * colours: the same black shirt read as far from itself, under ordinary camera
 * noise, as two genuinely different kits read from each other. Plenty of
 * players wear black. Four metrics were scored on how well they keep the SAME
 * kit together through camera noise and sun to shade, against how far apart
 * they hold DIFFERENT kits. This one, luma and chroma with brightness at a
 * quarter weight, was the most robust. The old one was the worst by a factor
 * of nearly five.
 *
 * WHAT NO METRIC CAN DO, measured and not assumed: tell a red shirt from a
 * maroon one once one of them walks into shade, because maroon is what red
 * looks like in shade. That is why colour is only ever used coarsely here. See
 * LOOK_DEAD_ZONE.
 */
export function lookDistance(a, b) {
  if (!a || !b) return null;
  const ycc = ([r, g, bl]) => { const Y = 0.299 * r + 0.587 * g + 0.114 * bl; return [Y, (bl - Y) * 0.564, (r - Y) * 0.713]; };
  let total = 0, parts = 0;
  for (const key of ['top', 'bottom']) {
    if (!Array.isArray(a[key]) || !Array.isArray(b[key])) continue;
    const p = ycc(a[key]), q = ycc(b[key]);
    total += Math.hypot(0.25 * (p[0] - q[0]), p[1] - q[1], p[2] - q[2]) / 255;
    parts++;
  }
  return parts ? total / parts : null;
}

/**
 * Below this, two outfits count as THE SAME and colour says nothing at all.
 *
 * Set at the measured noise floor: the distance the same kit reads from itself
 * at the 99th percentile, through camera noise and a sun to shade change.
 * Without it, two team mates in identical kit were tracked WORSE with the
 * colour cue on than with it off (up to 17 percent of crossings swapped,
 * against 7), because the cue carried no information for that pair, only
 * noise, and the noise was tipping the assignment at random. A cue that cannot
 * help must not be allowed to hurt. Inside the dead zone, movement decides
 * alone, exactly as if the colour had never been read.
 */
export const LOOK_DEAD_ZONE = 0.13;
const lookCost = (a, b) => { const d = lookDistance(a, b); return d === null ? 0 : Math.max(0, d - LOOK_DEAD_ZONE); };

/* ------------------------------------------------------- more than one body
 * MediaPipe can return several poses. It cannot tell you whose they are, and
 * neither can this. THERE IS NO FACE MATCHING HERE AND THERE WILL NOT BE: most
 * of the people in front of this camera are children, and recognising a child
 * by their face or body is biometric identification, which is a line this app
 * does not go near. A person says who a box is, once. What this code does is
 * keep that box on the same player for the rest of the run.
 *
 * It used to do that by proximity alone: whichever body was nearest to where a
 * body had been kept the slot. Two players crossing swapped slots, and in a
 * game players cross constantly, so their stats landed on each other. Three
 * things fix most of that, none of them about who anybody is:
 *
 *   WHERE THEY ARE HEADING. Each slot remembers its velocity and is matched
 *   against where it is expected to be, not where it was. Two players crossing
 *   are each still going the way they were going.
 *
 *   WHAT THEY ARE WEARING. The colour of the shirt and the shorts, handed in by
 *   the page, which can see pixels and this file cannot. It describes clothing,
 *   it lives in memory for the length of the run, and it is never saved.
 *
 *   THE WHOLE PICTURE AT ONCE. Bodies are paired with slots so the total cost
 *   is lowest, instead of best pair first, which is what forced swaps.
 *
 * WHERE IT STILL FAILS, because pretending otherwise would put a wrong number
 * on a child's record: two team mates in identical kit who cross while one is
 * hidden behind the other cannot be told apart by any of this. `crowded` is
 * still raised and the page still asks a human to check the names.
 */
/**
 * A body seen from the shoulders up, for the tracker: both shoulders seen and
 * the hips placed by the model, even if outside the picture. Returns the hip
 * anchor and a confidence, or null. Only called when poseFrame said no.
 */
export function upperBody(lm, T = TUNABLES) {
  if (!Array.isArray(lm) || lm.length < 25) return null;
  const ls = lm[LM.L_SHOULDER], rs = lm[LM.R_SHOULDER], lh = lm[LM.L_HIP], rh = lm[LM.R_HIP];
  if (!seen(ls, T) || !seen(rs, T)) return null;
  if (![lh, rh].every((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y))) return null;
  const x = (lh.x + rh.x) / 2, y = (lh.y + rh.y) / 2;
  // Hips placed somewhere absurd are not a body to follow.
  if (x < -0.5 || x > 1.5 || y < -0.5 || y > 2.5) return null;
  return { x, y, conf: 0.8 * Math.min(visOf(ls), visOf(rs)) };
}

export function createPoseTracker(T = TUNABLES) {
  const K = T.TRACK;
  let slots = [], retired = [], nextId = 1;

  /** Accept either [landmarks, landmarks] or a single landmarks array. */
  function asList(poses) {
    if (!Array.isArray(poses) || !poses.length) return [];
    const first = poses[0];
    if (Array.isArray(first)) return poses;
    if (first && typeof first === 'object' && typeof first.x === 'number') return [poses];
    return [];
  }

  /** Blend a new outfit reading into what is known, so one odd frame cannot rewrite it. */
  function blendLook(old, fresh) {
    if (!fresh) return old || null;
    if (!old) return { top: fresh.top ? [...fresh.top] : null, bottom: fresh.bottom ? [...fresh.bottom] : null };
    const mix = (a, b) => (a && b ? a.map((v, i) => v * 0.85 + b[i] * 0.15) : (a || b || null));
    return { top: mix(old.top, fresh.top), bottom: mix(old.bottom, fresh.bottom) };
  }

  return {
    get slots() { return slots.map((s) => ({ slot: s.slot, x: s.x, y: s.y, seenAt: s.seenAt, frames: s.frames })); },
    reset() { slots = []; retired = []; nextId = 1; },

    /**
     * `looks`, optional, is one outfit per pose in the same order as `poses`:
     * { top: [r,g,b], bottom: [r,g,b] } or null. Without it this still tracks,
     * on movement alone.
     */
    push(poses, t, { looks = null } = {}) {
      const list = asList(poses);
      const cands = [];
      list.forEach((lm, index) => {
        const look = Array.isArray(looks) ? (looks[index] || null) : null;
        const f = poseFrame(lm, T);
        if (f.ok) { cands.push({ index, lm, x: f.hipX, y: f.hipY, conf: f.conf, look }); return; }
        // A player close to the phone has their hips below the bottom of the
        // picture. They used to be dropped here, so the skeleton vanished and
        // no shot could be read the moment they stepped in, although a shot
        // needs only shoulders, elbows and wrists. The model still places the
        // hips it cannot see, just off the frame, so the body is kept, anchored
        // where the model put them. Its confidence is marked down, so a whole
        // body in view is still preferred when there is a choice.
        const up = upperBody(lm, T);
        if (up) cands.push({ index, lm, x: up.x, y: up.y, conf: up.conf, look, partial: true });
      });

      for (const s of slots) {
        if (t - s.seenAt > K.DROP_MS && !retired.some((r) => r.slot === s.slot)) retired.unshift(s);
      }
      slots = slots.filter((s) => t - s.seenAt <= K.DROP_MS);
      if (retired.length > K.MAX_SLOTS * 2) retired.length = K.MAX_SLOTS * 2;

      // Where each slot is expected to be by now.
      for (const s of slots) {
        const dt = Math.min(0.5, Math.max(0, (t - s.seenAt) / 1000));
        s.px = s.x + (s.vx || 0) * dt;
        s.py = s.y + (s.vy || 0) * dt;
      }

      // Every body against every slot, then the cheapest pairing overall.
      const BIG = 1e6;
      const cost = cands.map((c) => slots.map((s) => {
        const d = Math.hypot(c.x - s.px, c.y - s.py);
        if (d > K.GATE) return BIG;
        return d + K.COLOUR_WEIGHT * lookCost(c.look, s.look);
      }));
      const given = assignMinCost(cost);

      const tookC = new Set(), people = [];
      given.forEach((j, i) => {
        if (j < 0 || cost[i][j] >= BIG) return;
        const c = cands[i], sl = slots[j];
        const dt = Math.max(1 / 240, (t - sl.seenAt) / 1000);
        const moved = Math.hypot(c.x - sl.x, c.y - sl.y);
        // Velocity is kept as a running blend, so one jumpy frame cannot send
        // the prediction flying off in the wrong direction.
        const keep = K.VELOCITY_KEEP;
        sl.vx = (sl.vx || 0) * keep + ((c.x - sl.x) / dt) * (1 - keep);
        sl.vy = (sl.vy || 0) * keep + ((c.y - sl.y) / dt) * (1 - keep);
        sl.x = c.x; sl.y = c.y; sl.seenAt = t; sl.frames++;
        sl.look = blendLook(sl.look, c.look);
        tookC.add(c.index);
        people.push({ slot: sl.slot, index: c.index, lm: c.lm, x: c.x, y: c.y, conf: c.conf, isNew: false, moved: +moved.toFixed(3) });
      });

      for (const c of cands) {
        if (tookC.has(c.index)) continue;
        if (slots.length >= K.MAX_SLOTS) continue;

        // Somebody new, or somebody coming back? Two ways to decide, and both
        // are flagged so the page can ask a human to check.
        let back = null, by = null;
        if (cands.length === 1 && slots.length === 0) {
          // The old rule, kept: exactly one body and nobody else about.
          back = retired.find((r) => t - r.seenAt <= K.RESUME_MS) || null;
          if (back) by = 'alone';
        }
        if (!back && c.look) {
          // The new rule: the clothing matches one vanished body clearly, and
          // no other vanished body comes close. Both halves matter. A good
          // match that is only slightly better than the next is a coin toss.
          const scored = retired
            .filter((r) => t - r.seenAt <= K.REID_MS && r.look)
            .map((r) => ({ r, d: lookDistance(c.look, r.look) }))
            .filter((x) => x.d !== null)
            .sort((a, b) => a.d - b.d);
          const best = scored[0], next = scored[1];
          if (best && best.d <= K.REID_MAX && (!next || next.d - best.d >= K.REID_MARGIN)) { back = best.r; by = 'clothing'; }
        }

        const slot = back ? back.slot : nextId++;
        if (back) retired = retired.filter((r) => r.slot !== back.slot);
        slots.push({ slot, x: c.x, y: c.y, px: c.x, py: c.y, vx: 0, vy: 0, seenAt: t, frames: back ? back.frames : 1, look: blendLook(back ? back.look : null, c.look) });
        people.push({ slot, index: c.index, lm: c.lm, x: c.x, y: c.y, conf: c.conf, isNew: !back, resumed: Boolean(back), resumedBy: by, moved: 0 });
      }

      // Two bodies close together are still where this goes wrong. Say so.
      let crowded = false;
      for (let i = 0; i < cands.length && !crowded; i++) {
        for (let j = i + 1; j < cands.length; j++) {
          if (Math.hypot(cands[i].x - cands[j].x, cands[i].y - cands[j].y) <= K.GATE) { crowded = true; break; }
        }
      }
      people.sort((a, b) => a.slot - b.slot);
      return { people, crowded, count: cands.length, dropped: list.length - cands.length };
    },
  };
}

/* ---------------------------------------------------------- which body is YOU
 * The player is a slot in the tracker above, not an array position. Whoever
 * has been in the picture longest is the player: they set the camera down and
 * stepped back, so they were there first. Once chosen they stay chosen, and a
 * passer by never takes over. Only if the player is gone for LOCK.GONE_MS does
 * it look again, except when the player is plainly alone (LOCK.HANDOFF_*).
 *
 *   const lock = createPlayerLock();
 *   const { me, switched } = lock.pick(tracked.people, tracker.slots, t);
 *
 * `switched` is true when the player is now a different slot than before, so
 * the caller can restart anything it was smoothing across frames.
 */
export function createPlayerLock(T = TUNABLES) {
  const K = T.LOCK;
  let slot = null, seenAt = 0, at = null;
  const firstSeen = new Map();          // slot -> when it first came into view
  return {
    get slot() { return slot; },
    reset() { slot = null; seenAt = 0; at = null; firstSeen.clear(); },

    pick(people, slots, now) {
      people = Array.isArray(people) ? people : [];
      slots = Array.isArray(slots) ? slots : [];
      for (const p of people) if (!firstSeen.has(p.slot)) firstSeen.set(p.slot, now);
      if (firstSeen.size > 64) for (const k of firstSeen.keys()) if (!slots.some((s) => s.slot === k)) firstSeen.delete(k);
      const take = (p) => {
        const switched = p.slot !== slot;
        slot = p.slot; seenAt = now; at = { x: p.x, y: p.y };
        return { me: p, switched };
      };
      const mine = slot === null ? null : people.find((p) => p.slot === slot);
      if (mine) return take(mine);
      if (slot !== null && now - seenAt < K.GONE_MS) {
        // Alone: one body, no one else tracked, new since the player went, close
        // by, and the player out of view long enough not to be a missed frame.
        const [p] = people;
        const alone = people.length === 1
          && now - seenAt >= K.HANDOFF_WAIT_MS
          && slots.every((s) => s.slot === slot || s.slot === p.slot)
          && (firstSeen.get(p.slot) ?? now) >= seenAt - K.HANDOFF_MS
          && at && Number.isFinite(p.x) && Number.isFinite(p.y)
          && Math.hypot(p.x - at.x, p.y - at.y) <= K.HANDOFF_DIST;
        return alone ? take(p) : { me: null, switched: false };
      }
      if (!people.length) return { me: null, switched: false };
      const age = (p) => slots.find((s) => s.slot === p.slot)?.frames || 0;
      return take([...people].sort((a, b) => age(b) - age(a) || (b.conf || 0) - (a.conf || 0))[0]);
    },
  };
}

/* ---------------------------------------------------------- one player, all
 * Every detector above, driven off one stream of landmarks, plus the two
 * things that only make sense when they all run together: a jump proxy
 * measured against this player's own standing hips, and the fatigue tracker
 * that every rep is fed into.
 */
export function createPlayerScorer(T = TUNABLES) {
  let tun = T;
  const standY = createLevelTracker({ windowMs: 4000, p: 0.6 });
  const fatigue = createFatigueTracker(T);
  let standFeetLevel = createLevelTracker({ windowMs: 5000, p: 0.85 });
  let jumpNow = 0, jumpRecent = [], quality = null, reps = [], lastAt = 0;
  let shot, dribble, balance, stance, boxout, drive, pivot, slide, footwork;

  function build() {
    shot = createShotDetector(tun);
    dribble = createDribbleDetector(tun);
    balance = createBalanceDetector(tun);
    stance = createStanceDetector(tun);
    boxout = createBoxoutDetector(tun, { standLevel: () => standFeetLevel.level });
    drive = createDriveDetector(tun, { standHipY: () => standY.level });
    pivot = createPivotDetector(tun);
    slide = createSlideDetector(tun);
    footwork = createFootworkDetector(tun);
  }
  build();

  const keep = (rep) => { reps.push(rep); if (reps.length > 600) reps.shift(); fatigue.note(rep); return rep; };

  function jumpAround() {
    const vals = jumpRecent.map((r) => r.v);
    return vals.length ? Math.max(...vals) : 0;
  }

  return {
    get tunables() { return tun; },
    get calibrated() { return Boolean(tun.CALIBRATED); },
    get reps() { return reps.slice(); },
    get live() {
      return {
        quality,
        jumpRise: +jumpNow.toFixed(3),
        shot: shot.last, dribble: dribble.last, balance: balance.last,
        stance: stance.last, boxout: boxout.last, drive: drive.last,
        inStance: stance.active, inBoxout: boxout.active, driving: drive.active,
        pivot: pivot.last, slide: slide.last, footwork: footwork.last,
        pivoting: pivot.active, sliding: slide.active, stepping: footwork.active,
      };
    },

    /** Swap in calibrated thresholds. Reps already taken keep their own. */
    setTunables(next) { tun = next || TUNABLES; build(); },

    reset() {
      reps = []; jumpRecent = []; jumpNow = 0; quality = null;
      standY.reset(); standFeetLevel.reset(); fatigue.reset(); build();
    },

    push(lm, t) {
      lastAt = t;
      quality = frameQuality(lm, tun);
      const f = poseFrame(lm, tun);
      if (f.ok) {
        standY.push(t, f.hipY);
        if (f.hipOverFeet !== null) standFeetLevel.push(t, f.hipOverFeet);
        const level = standY.level;
        jumpNow = level === null ? 0 : Math.max(0, (level - f.hipY) / f.scale);
        jumpRecent.push({ t, v: jumpNow });
        while (jumpRecent.length && t - jumpRecent[0].t > 900) jumpRecent.shift();
      }

      const out = [];
      const s = shot.push(lm, t);
      if (s) {
        out.push(keep({
          kind: 'shot', at: s.releasedAt ?? t, source: 'measured', calibrated: Boolean(tun.CALIBRATED),
          metrics: { ...s.metrics, jumpRise: +jumpAround().toFixed(3) },
          scores: s.scores, overall: s.overall, side: s.side,
          made: null,             // a human decides this, and only a human
          shot: s,                // the original shape the session API wants
        }));
      }
      for (const det of [dribble, balance, stance, boxout, drive, pivot, slide, footwork]) {
        const rep = det.push(lm, t);
        if (rep) out.push(keep(rep));
      }
      return out;
    },

    /** End of session: anything still being held counts. */
    flush() {
      const out = [];
      for (const det of [stance, boxout, drive, pivot, slide, footwork]) {
        const rep = det.flush?.();
        if (rep) out.push(keep(rep));
      }
      return out;
    },

    fatigue: (nowMs = null) => fatigue.report(nowMs === null ? lastAt : nowMs),
    summary: () => ({
      ...summarizeReps(reps),
      calibrated: Boolean(tun.CALIBRATED),
      calibration: tun.CALIBRATED || null,
      fatigue: fatigue.report(lastAt),
    }),
  };
}

/** Counts and averages per kind, for a HUD, a report or a run card. */
export function summarizeReps(reps) {
  const list = Array.isArray(reps) ? reps : [];
  const counts = {}, avg = {};
  const KEYS = {
    shot: ['elbowOffset', 'releaseAngle', 'releaseRise', 'followFrames', 'followMs', 'jumpRise'],
    dribble: ['lowHeight', 'amplitude', 'intervalMs', 'cadenceHz'],
    balance: ['offset', 'sway', 'lean', 'outOfBasePct'],
    stance: ['holdMs', 'kneeMean', 'widthRatio', 'handsUpPct'],
    boxout: ['holdMs', 'kneeMean', 'widthRatio', 'armSpread', 'hipDrop'],
    drive: ['firstStepLen', 'firstStepMs', 'peakSpeed', 'kneePush', 'jumpRise'],
    pivot: ['holdMs', 'plantDrift', 'freeTravel', 'hipTurnDeg', 'confidence'],
    slide: ['durationMs', 'distance', 'lateralSpeed', 'crossings', 'kneeMean', 'widthRatio', 'directionChanges'],
    footwork: ['steps', 'stepsPerSec', 'avgStepLen', 'directionChanges'],
  };
  for (const kind of Object.keys(KEYS)) {
    const of = list.filter((r) => r.kind === kind);
    counts[kind] = of.length;
    if (!of.length) continue;
    const row = { overall: round(mean(of.map((r) => r.overall).filter(Number.isFinite))) };
    for (const key of KEYS[kind]) {
      const vals = of.map((r) => r.metrics?.[key]).filter((v) => Number.isFinite(v));
      if (vals.length) row[key] = +mean(vals).toFixed(3);
    }
    row.n = of.length;
    avg[kind] = row;
  }
  return { source: 'measured', total: list.length, counts, avg };
}

/* ------------------------------------------------------------------- crew
 * One scorer per slot, so a game, an open run or a team practice can score
 * more than one body at once. Slots are positions in the picture, never
 * people: whoever is using the app assigns the names.
 */
export function createCrew(T = TUNABLES) {
  let tun = T;
  const tracker = createPoseTracker(T);
  const scorers = new Map();
  const smoothers = new Map();

  function scorerFor(slot) {
    if (!scorers.has(slot)) {
      const s = createPlayerScorer(tun);
      scorers.set(slot, s);
    }
    return scorers.get(slot);
  }

  return {
    get slots() { return [...scorers.keys()]; },
    scorer: scorerFor,
    setTunables(next) { tun = next || TUNABLES; for (const s of scorers.values()) s.setTunables(next); },
    reset() { tracker.reset(); scorers.clear(); smoothers.clear(); },

    push(poses, t, opts = {}) {
      const { people, crowded, count, dropped } = tracker.push(poses, t, opts);
      const reps = [];
      for (const p of people) {
        // Steadied per body, AFTER the tracker has decided whose body it is: a
        // filter that carried over from one player to another would drag the
        // second player's points toward where the first one had been.
        if (!smoothers.has(p.slot)) smoothers.set(p.slot, createLandmarkSmoother());
        if (p.isNew) smoothers.get(p.slot).reset();
        p.raw = p.lm;
        p.lm = smoothers.get(p.slot).push(p.lm, t);
        const scorer = scorerFor(p.slot);
        for (const rep of scorer.push(p.lm, t)) reps.push({ slot: p.slot, ...rep });
      }
      return { people, reps, crowded, count, dropped };
    },

    flush() {
      const reps = [];
      for (const [slot, scorer] of scorers) for (const rep of scorer.flush()) reps.push({ slot, ...rep });
      return reps;
    },

    summary() {
      const out = {};
      for (const [slot, scorer] of scorers) out[slot] = scorer.summary();
      return out;
    },
  };
}

/* ------------------------------------------------------------ rim and zones
 * Distance from the shooter to a rim the player marked on the preview. The
 * shooter's shoulder width is the ruler (about forty centimetres on an adult),
 * the gap from their feet to the rim mark is measured in that ruler, and the
 * rim's height is taken back out to leave the distance along the floor. A
 * camera estimate: honest to a few feet when the camera sees the court from
 * the side, rougher looking down the court, because one camera cannot see
 * depth. Pure, so the arithmetic can be tested without a browser.
 */
export const RIM = { SHOULDER_M: 0.41, RIM_M: 3.05, FT_PER_M: 3.281, MAX_FT: 60, MIN_SHOULDER_PX: 8 };
export const zoneOf = (ft) => (ft < 5 ? 'rim' : ft < 11 ? 'paint' : ft < 18 ? 'mid' : ft < 22 ? 'long-two' : 'three');
/**
 * @param lm     pose landmarks in frame units (unmirrored)
 * @param rim    { x, y } in the same frame units
 * @param width  frame width in pixels, height likewise: frame units are not square
 * @returns { distanceFt, zone } or null when the body is not there to measure
 */
export function rimDistance(lm, rim, width, height) {
  if (!rim || !lm) return null;
  const a = lm[11], b = lm[12], l = lm[27], r = lm[28];
  if (!a || !b || !l || !r || (l.visibility ?? 1) < 0.4 || (r.visibility ?? 1) < 0.4) return null;
  const shoulderPx = Math.hypot((a.x - b.x) * width, (a.y - b.y) * height);
  if (shoulderPx < RIM.MIN_SHOULDER_PX) return null;
  const feet = { x: (l.x + r.x) / 2, y: (l.y + r.y) / 2 };
  const gapPx = Math.hypot((feet.x - rim.x) * width, (feet.y - rim.y) * height);
  const straightM = gapPx / (shoulderPx / RIM.SHOULDER_M);
  const groundM = Math.sqrt(Math.max(0, straightM * straightM - RIM.RIM_M * RIM.RIM_M));
  const distanceFt = Math.round(groundM * RIM.FT_PER_M);
  if (!Number.isFinite(distanceFt) || distanceFt > RIM.MAX_FT) return null;
  return { distanceFt, zone: zoneOf(distanceFt) };
}
